/**
 * The showcase's reference documentation, as data.
 *
 * The page had always been a GALLERY: live exhibits with variant labels, and prose that
 * read as a changelog rather than as guidance — "Danger — the destructive outline ACTION
 * (§ 84)", "compact was inert until § 65 made it real". Someone arriving to answer "which
 * component do I reach for, and what props does it take" could not answer either question
 * without opening the source, which is the one thing a showcase exists to prevent.
 *
 * So the reference lives here, beside the exhibits but not inside them: `DocBlock.tsx`
 * renders an entry, `DesignSystemShowcase.tsx` drops one under each section heading, and
 * this file stays a flat list a human can read end to end.
 *
 * Why a separate module rather than JSX in the page: the page is already 4,700 lines, and
 * docs-as-data can be CHECKED. `componentDocs.test.tsx` asserts every component directory
 * under `DesignSystem/` has an entry, every entry names a section that is really on the
 * page, and — the one that matters — every documented prop name exists in the matching
 * `types/DesignSystem/*.ts`. A renamed prop fails a test instead of quietly becoming a
 * lie on the page that teaches it.
 *
 * House rule for the copy here: NO section numbers. `§ 209` means something to the commit
 * that introduced it and nothing to the reader who needs to know that `loading` blocks
 * the click. The numbers stay in source comments, where they are the audit trail back to
 * `okf/design-system/e2e.md`; they never reach the rendered page.
 */

/** One row of a props table. */
export interface PropDoc {
  /**
   * Exactly as it is spelled in the type file — `componentDocs.test.tsx` looks it up
   * there, so a typo here is a failing test rather than a wrong doc.
   */
  name: string;
  /** The type a caller writes, not the internal alias chain. */
  type: string;
  /** Literal default. Omit when there is none. */
  default?: string;
  /** Required props render "required" where the default would go. */
  required?: boolean;
  /**
   * A native attribute that reaches the underlying element through the rest-prop
   * spread rather than being declared in the component's own props interface.
   * Exempt from the type-file lookup, because it is correctly not declared there.
   */
  native?: boolean;
  /** Kept in the table so call sites can find it; struck through, with the way out. */
  deprecated?: string;
  /** One sentence a caller can act on. Not a description of the implementation. */
  description: string;
}

export interface ComponentDoc {
  /** Display name, and the import binding. */
  name: string;
  /** The showcase section that demos it. Several components may share one. */
  section: string;
  /** Copy-pasteable, relative to `src/`. */
  importPath: string;
  /** What it is, in one sentence. */
  summary: string;
  /** When to reach for it. */
  useWhen: string[];
  /** Where to go instead, and when. The half of a doc that prevents misuse. */
  insteadUse: { component: string; when: string }[];
  /** The props table. Empty renders no table — correct for a component that takes none. */
  props: PropDoc[];
  /** A minimal working usage. Real prop values, not `foo`. */
  snippet: string;
  /** The file `props` are checked against. `null` for a component with no own type file. */
  typeFile: string | null;
  /**
   * How a caller reaches it, which decides what its snippet must show.
   *
   * - `element` (default) — rendered as JSX. `Toast` is not one of these.
   * - `api` — raised imperatively through a camelCase binding (`toast.success(…)`).
   * - `module` — a set of named exports rather than one component (`Iconography`),
   *   so the snippet shows the import it is used through.
   *
   * Three real shapes, written into the type rather than smuggled past the snippet
   * check as special cases — a check with exceptions hidden in it stops being one.
   */
  usage?: 'element' | 'api' | 'module';
}

export const COMPONENT_DOCS: ComponentDoc[] = [
  /* ── Layout & density ────────────────────────────────────────────────────── */
  {
    name: 'DensityProvider',
    section: 'density',
    importPath: 'Components/DesignSystem/DensityProvider/DensityProvider',
    typeFile: 'density.ts',
    summary:
      'Sets the default control size for a whole region, so a dense screen does not have to say size="sm" at every call site.',
    useWhen: [
      'A whole screen or panel should be denser or roomier than the app default — a 100-row grid and the toolbar above it.',
      'You would otherwise pass the same size prop to every control in a region.',
    ],
    insteadUse: [
      { component: 'Button', when: 'one control differs from its neighbours — pass its own size, which always wins' },
      { component: 'Table', when: 'only the rows change density and the surrounding chrome does not' },
    ],
    props: [
      { name: 'density', type: "'compact' | 'default' | 'comfortable'", description: 'Omit to INHERIT the surrounding region rather than reset to default — so a wrapper added for another reason cannot silently un-compact its subtree.' },
      { name: 'children', type: 'React.ReactNode', description: 'Renders no DOM of its own: a wrapper element would become the flex item and change layout.' },
    ],
    snippet: `<DensityProvider density="compact">
  <Toolbar />
  <Table columns={columns} data={rows} rowKey="id" />
</DensityProvider>`,
  },
  /* ── Data expression ──────────────────────────────────────────────────────── */
  {
    name: 'Sparkline',
    section: 'data-expression',
    importPath: 'Components/DesignSystem/Sparkline/Sparkline',
    typeFile: 'dataviz.ts',
    summary:
      'A trend in the space a word would take. Sits in a table cell or beside a figure, where a chart would be absurd.',
    useWhen: [
      'A number has a history worth seeing, and the shape matters more than any single reading.',
      'The trend has to fit inside a row, a cell, or a Stat.',
    ],
    insteadUse: [
      { component: 'Progress', when: 'the number is a fraction of a known whole, not a series over time' },
      { component: 'DistributionBar', when: 'the question is how a total divides, not how it moved' },
    ],
    props: [
      { name: 'values', type: 'number[]', required: true, description: 'Oldest first. Fewer than two points, or every value identical, draws a flat line — never an empty box and never a NaN path.' },
      { name: 'label', type: 'string', description: 'Announced name. Give it one unless the same numbers sit in text beside the line.' },
      { name: 'decorative', type: 'boolean', default: 'false', description: 'Says the line repeats adjacent text, so it is hidden from assistive technology instead of read out twice.' },
      { name: 'series', type: 'number', default: '0', description: 'Which categorical series to paint in — a position, not a colour. Wraps past the sixth.' },
      { name: 'width', type: 'number', default: '96', description: 'Drawn width in px. The viewBox matches it, so the stroke is never sheared.' },
      { name: 'height', type: 'number', default: '24', description: 'Drawn height in px.' },
      { name: 'area', type: 'boolean', default: 'false', description: 'Fills under the line. Off in a table — a column of fills is noise; on at page level.' },
      { name: 'endpoint', type: 'boolean', default: 'true', description: 'Dots the last point, which is the value the reader is actually looking for.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the svg.' },
      { name: 'data-testid', type: 'string', description: 'Templates `-line`, `-area` and `-endpoint`.' },
    ],
    snippet: `<Sparkline
  values={model.impressions}
  label="Impressions, last 14 days"
  series={0}
  data-testid="model-impressions"
/>`,
  },
  {
    name: 'Delta',
    section: 'data-expression',
    importPath: 'Components/DesignSystem/Delta/Delta',
    typeFile: 'dataviz.ts',
    summary:
      'A signed change with its meaning attached. The one prop that matters is whether a rise is good news.',
    useWhen: [
      'A figure is being compared to a previous period.',
      'The direction of the change carries a verdict the reader should not have to work out.',
    ],
    insteadUse: [
      { component: 'Status', when: 'the state is a category, not a movement' },
      { component: 'Badge', when: 'it is a count rather than a change' },
    ],
    props: [
      { name: 'value', type: 'number', required: true, description: 'The signed change. Zero drops the arrow and takes the neutral tone — "no change" must not look like a tiny rise.' },
      { name: 'sentiment', type: "'up-is-good' | 'up-is-bad' | 'neutral'", default: "'up-is-good'", description: 'Set `up-is-bad` for latency, cost, error rate and queue depth. Leaving the default makes a regression render as an improvement.' },
      { name: 'format', type: "'percent' | 'point' | 'absolute'", default: "'percent'", description: '`point` is a change IN a percentage, which is not a percentage change — dashboards lose that distinction constantly.' },
      { name: 'unit', type: 'string', default: "''", description: 'Appended for `absolute`. Written verbatim, so include any leading space.' },
      { name: 'precision', type: 'number', description: 'Decimal places. Defaults to 1 for percent and point, 0 for absolute.' },
      { name: 'quiet', type: 'boolean', default: 'false', description: 'Tone as ink instead of a filled pill. Use in a table column, where a stack of pills out-shouts the numbers.' },
      { name: 'label', type: 'string', description: 'What the change is OF, for the announced string. Without it a screen reader hears a direction with no subject.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Templates `-arrow` and `-value`.' },
    ],
    snippet: `<Delta
  value={0.6}
  sentiment="up-is-bad"
  format="absolute"
  unit="s"
  label="Median build time"
/>`,
  },
  {
    name: 'Stat',
    section: 'data-expression',
    importPath: 'Components/DesignSystem/Stat/Stat',
    typeFile: 'dataviz.ts',
    summary:
      'A figure with its name and its comparison. The number is the loud part; the label is not a heading.',
    useWhen: [
      'A page opens with a handful of measures the reader scans before anything else.',
      'A figure needs a comparison or a trend attached without becoming a chart.',
    ],
    insteadUse: [
      { component: 'Card', when: 'the tile holds prose or controls rather than one figure' },
      { component: 'Table', when: 'there are more than about six measures — that is a list' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'What the number is, in sentence case. Rendered quiet and small, because the eye should land on the figure first.' },
      { name: 'value', type: 'React.ReactNode', required: true, description: 'Already formatted — locale, currency and unit are yours. Rendered in tabular figures so a polling tile cannot judder.' },
      { name: 'caption', type: 'React.ReactNode', description: 'The comparison beneath. Usually a Delta, sometimes just "vs. last 30 days".' },
      { name: 'trend', type: 'React.ReactNode', description: 'A Sparkline for the same measure.' },
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: '`sm` for a summary bar above a table, where the figure must not out-shout the page title.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Templates `-label`, `-value` and `-caption`.' },
    ],
    snippet: `<Stat
  label="Active guides"
  value="1,284"
  caption={<Delta value={6.2} label="Active guides" />}
  data-testid="kpi-active-guides"
/>`,
  },
  {
    name: 'DistributionBar',
    section: 'data-expression',
    importPath: 'Components/DesignSystem/DistributionBar/DistributionBar',
    typeFile: 'dataviz.ts',
    summary:
      'How a whole divides, in one row. The stacked-bar-of-one, for where a pie would need its own panel.',
    useWhen: [
      'A total splits into a handful of named parts and the shares are the point.',
      'The split has to fit in a table cell or under a heading.',
    ],
    insteadUse: [
      { component: 'Progress', when: 'there is one fraction, not a division into parts' },
      { component: 'Sparkline', when: 'the question is movement over time' },
    ],
    props: [
      { name: 'segments', type: 'DistributionSegment[]', required: true, description: 'In display order. Zero and negative values are dropped rather than drawn as hairlines the reader counts as categories.' },
      { name: 'total', type: 'number', description: 'The denominator when the segments do not cover it. The shortfall becomes a quiet rest slice — without it the bar silently rescales and misstates every share.' },
      { name: 'label', type: 'string', required: true, description: 'Announced name: "Catalogue by category". The slices themselves are hidden shapes.' },
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: '6px in a row, 10px at page level.' },
      { name: 'showLegend', type: 'boolean', default: 'false', description: 'Swatch, label and share beneath. Off in a table cell; on at page level.' },
      { name: 'restLabel', type: 'string', default: "'Other'", description: 'Names the computed shortfall slice.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Templates `-track`, `-segment-${index}` and `-legend`.' },
    ],
    snippet: `<DistributionBar
  label="Catalogue by category"
  segments={categories.map((c) => ({ label: c.name, value: c.count }))}
  total={catalogueSize}
  showLegend
/>`,
  },
  /* ── Actions ──────────────────────────────────────────────────────────────── */
  {
    name: 'Button',
    section: 'buttons',
    importPath: 'Components/DesignSystem/Button/Button',
    typeFile: 'Button.ts',
    summary:
      'The labelled action. Its variant is a rank, not a colour choice — exactly one primary per surface, and everything else steps down from there.',
    useWhen: [
      'A user performs an action: submit, apply, open a dialog, run an import.',
      'The action needs a word. A glyph alone would be a guess.',
    ],
    insteadUse: [
      { component: 'IconButton', when: 'the action is one glyph in a toolbar or a table row' },
      { component: 'Link', when: 'it navigates somewhere rather than doing something' },
      { component: 'DropdownButton', when: 'one trigger offers a choice of actions' },
    ],
    props: [
      {
        name: 'variant',
        type: "'primary' | 'secondary' | 'tertiary' | 'subtle' | 'danger' | 'danger-primary'",
        default: "'primary'",
        description:
          'The rank. `danger` is the destructive action offered beside a safe one; `danger-primary` is the filled confirm inside the dialog that follows. They are not interchangeable.',
      },
      {
        name: 'size',
        type: "'sm' | 'md' | 'lg'",
        default: "'md'",
        description: 'Height on the shared control grid — 28 / 36 / 44px. Density only; the label never changes size.',
      },
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The label. Write the verb the user is performing, not "OK".' },
      { name: 'startIcon', type: 'React.ReactNode', description: 'A glyph before the label. Design-system icons draw in currentColor, so it takes the variant ink for free. Shows whenever it is given.' },
      { name: 'endIcon', type: 'React.ReactNode', description: 'A glyph after the label — a "Next", an external-link mark. May be passed with `startIcon`.' },
      { name: 'iconSize', type: "'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large'", default: "'medium'", description: 'Glyph box for both slots — 16 / 20 / 24px. `sm | md | lg` match `size`; the long words are the older spelling of the same boxes.' },
      {
        name: 'badge',
        type: 'string | number',
        description:
          'A count rendered as a sibling of the label, whenever it has a value (0 included). Use this rather than passing a Badge as a child — a Badge inside the label span breaks onto its own line and stretches full width.',
      },
      {
        name: 'icon',
        type: 'React.ReactNode',
        default: 'null',
        deprecated: 'Pass `startIcon`, or `endIcon` for the end — one slot per side. `node scripts/ds-api-codemod.mjs --write <path>` folds it.',
        description: 'Still honoured, with `iconPosition` and `showIcon`, drawing exactly what the slot draws.',
      },
      { name: 'iconPosition', type: "'start' | 'end'", default: "'start'", deprecated: 'The slot says the side: `startIcon` / `endIcon`.', description: 'Still honoured.' },
      { name: 'showIcon', type: 'boolean', default: 'true', deprecated: 'A slot shows when it is given. Every app call site set this to a value that changed nothing.', description: 'Still honoured: `false` hides an `icon`.' },
      { name: 'showBadge', type: 'boolean', default: 'false', deprecated: 'Pass `badge`, which needs no switch.', description: 'Still honoured, with `badgeContent`.' },
      { name: 'badgeContent', type: 'string | number', deprecated: 'Pass `badge`.', description: 'Still honoured, shown only with `showBadge`.' },
      { name: 'badgeVariant', type: "'warning' | 'primary' | 'secondary' | 'disabled' | 'neutral' | 'selected'", description: 'Overrides the badge rank derived from `variant` and `disabled`.' },
      {
        name: 'loading',
        type: 'boolean',
        default: 'false',
        description:
          'The async state: a spinner takes the icon slot, the label stays, and the click is refused and announced (`aria-disabled` + `aria-busy`). Not natively disabled, so focus stays on the button; a `type="submit"` still cannot submit. Additive — nothing moves while it is unset. A button with no icon grows by the spinner\'s slot while it loads — the price of keeping a label like "Saving…" visible.',
      },
      { name: 'disabled', type: 'boolean', default: 'false', native: true, description: 'Native. Blocks the click and drops the ink to the disabled role.' },
      { name: 'focusableWhenDisabled', type: 'boolean', default: 'false', description: 'With `disabled`: same disabled look, click and submit still blocked, but `aria-disabled` instead of the native attribute, so a button that disables itself while it holds focus (a Save while saving) keeps it. No effect without `disabled`.' },
      { name: 'onClick', type: '(e: React.MouseEvent) => void', native: true, description: 'Native. Everything not listed above reaches the underlying button element. Not called while `loading`, or while `disabled` + `focusableWhenDisabled`.' },
      { name: 'className', type: 'string', native: true, description: 'Merged with `cn()`: a class that conflicts with a Button class replaces it — `tw-px-2` replaces the size inset — rather than losing on stylesheet order.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook. The badge derives `${testid}-badge` from it.' },
      {
        name: 'ariaLabel',
        type: 'string',
        deprecated: 'Pass `aria-label` directly — it reaches the element like any other native attribute, and wins over this.',
        description: 'Still honoured, so existing call sites need no change.',
      },
    ],
    snippet: `<Button
  variant="primary"
  startIcon={<Save />}
  loading={isSaving}
  onClick={handleSave}
  data-testid="attribute-save-btn"
>
  Save attribute
</Button>`,
  },

  {
    name: 'ButtonLink',
    section: 'buttons',
    importPath: 'Components/DesignSystem/Button/ButtonLink',
    typeFile: 'ButtonLink.ts',
    summary:
      'Navigation that looks like a Button and IS a link: a real anchor (or a router Link) in Button\'s variants, sizes, icon slot and focus ring — so it can be middle-clicked, opened in a new tab, and is announced as a link.',
    useWhen: [
      'The control goes somewhere — "Create rule", "Back to results", "View in catalogue" — and deserves a button\'s weight.',
      'You are about to write a Button whose onClick only calls navigate().',
    ],
    insteadUse: [
      { component: 'Button', when: 'the control DOES something: saves, deletes, opens a dialog' },
      { component: 'Link', when: 'the destination sits inside running text' },
    ],
    props: [
      { name: 'href', type: 'string', description: 'The destination, on a real anchor. Omit it only with `as`, which takes `to` instead.' },
      { name: 'as', type: 'React.ElementType', description: 'A router Link for client-side navigation: `as={RouterLink} to="/rules/new"`. The design system cannot import the router itself.' },
      { name: 'to', type: 'string', description: 'Handed to the `as` element — a router Link\'s destination.' },
      { name: 'variant', type: "'primary' | 'secondary' | 'tertiary' | 'subtle' | 'danger' | 'danger-primary'", default: "'primary'", description: 'Button\'s variants, drawn by Button\'s own resolver, so the two cannot drift. The ink is restated under the pointer, because the app\'s global link styles would otherwise recolour it.' },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Height on the control grid — 28 / 36 / 44px.' },
      { name: 'startIcon', type: 'React.ReactNode', description: 'A glyph before the label, as on Button.' },
      { name: 'endIcon', type: 'React.ReactNode', description: 'A glyph after the label. May be passed with `startIcon`.' },
      { name: 'iconSize', type: "'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large'", default: "'medium'", description: 'Glyph box for both slots — 16 / 20 / 24px, as on Button.' },
      { name: 'external', type: 'boolean', default: 'false', description: 'Opens a new tab with rel="noopener noreferrer", and says so to a screen reader.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'A link cannot be natively disabled: this removes the destination, keeps the link role, marks it aria-disabled and takes it out of the tab order.' },
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The label. Say where it goes.' },
      { name: 'className', type: 'string', default: "''", description: 'Merged with `withClassName`: a conflicting class replaces the component\'s.' },
      { name: 'onClick', type: '(e: React.MouseEvent) => void', native: true, description: 'Native — everything not listed above, and the ref, reach the anchor (`download`, `hrefLang`…).' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<ButtonLink as={RouterLink} to="/rules/new" startIcon={<Add />} data-testid="rules-create">
  Create rule
</ButtonLink>`,
  },

  {
    name: 'IconButton',
    section: 'icon-button',
    importPath: 'Components/DesignSystem/IconButton/IconButton',
    typeFile: 'IconButton.ts',
    summary:
      'The square, glyph-only action — a real button with a focus ring, an accessible name and a full-size hit target, which an SVG in a clickable div has none of.',
    useWhen: [
      'A toolbar or table row needs an action and there is no room for a word.',
      'You are about to write onClick on a div wrapping an icon. This is what that becomes.',
    ],
    insteadUse: [
      { component: 'Button', when: 'the action deserves a label — most of them do' },
      { component: 'Menu', when: 'the glyph would open a list of actions rather than run one' },
    ],
    props: [
      { name: 'loading', type: 'boolean', default: 'false', description: 'The async state: a small spinner takes the glyph, the click is refused and announced (`aria-disabled` + `aria-busy`), the variant keeps its resting shape with the hover off. Not natively disabled, so a focused button keeps focus — the Button rule.' },
      {
        name: 'aria-label',
        type: 'string',
        required: true,
        description:
          'Required by the type, not by a lint. A glyph-only button with no name is a mystery to a screen reader, so the compiler asks for one.',
      },
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The glyph. Design-system icons inherit the variant ink through currentColor.' },
      {
        name: 'variant',
        type: "'subtle' | 'secondary' | 'danger-subtle'",
        default: "'subtle'",
        description:
          '`subtle` is the quiet toolbar or row action; `secondary` is the bordered standalone; `danger-subtle` is the row delete — quiet until hovered, then unmistakably red.',
      },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Square on the control grid — 28 / 36 / 44px both ways.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks the click and drops the glyph to the disabled ink.' },
      { name: 'className', type: 'string', default: "''", description: 'Appended last. For placement, not for re-colouring the variant.' },
      { name: 'tooltip', type: 'boolean', default: 'false', description: 'Shows the aria-label in the system Tooltip on hover and keyboard focus, so a sighted user is told what the glyph does — the app\'s icon buttons have no visible name otherwise. Not doubled as a description for a screen reader. The button sits in the Tooltip\'s wrapper while it is on.' },
      { name: 'tooltipPlacement', type: "'top' | 'bottom' | 'left' | 'right'", default: "'top'", description: 'Which side the tooltip opens on. It still flips when that side has no room.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<IconButton
  aria-label="Delete rule"
  variant="danger-subtle"
  onClick={() => remove(rule.id)}
  data-testid={\`rule-\${rule.id}-delete\`}
>
  <Delete />
</IconButton>`,
  },

  {
    name: 'DropdownButton',
    section: 'dropdown-button',
    importPath: 'Components/DesignSystem/DropdownButton/DropdownButton',
    typeFile: 'DropdownButton.ts',
    summary:
      'One trigger, several actions. The label names the job, the list holds the ways to do it — and unlike a Dropdown, picking one runs it rather than storing it.',
    useWhen: [
      'A toolbar offers Sort, Filter or Bulk actions: a family of commands under one control.',
      'Choosing an option performs it immediately and the trigger keeps its own label.',
    ],
    insteadUse: [
      { component: 'Dropdown', when: 'the choice is a VALUE the form keeps and the trigger should show it' },
      { component: 'Menu', when: 'you need sections, radio items, shortcuts or a footer' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'The trigger text. It names the family of actions and does not change with the selection.' },
      { name: 'options', type: 'DropdownButtonOption[]', required: true, description: '`{ id, label, icon?, disabled? }`. Each row is an action. A disabled row is aria-disabled — the arrows reach it, a pick is refused — and opening lands on the first enabled row.' },
      { name: 'onSelect', type: '(optionId: string | number) => void', description: 'Fires with the chosen option id. Run the action here.' },
      {
        name: 'variant',
        type: "'primary' | 'secondary' | 'tertiary' | 'subtle'",
        default: "'primary'",
        description: 'The same four looks as Button, painted from Button\'s own variant table, so a button never changes its look when it gains a menu. `subtle` is the quiet toolbar trigger — ink rather than accent, so a row of them does not read as a row of links.',
      },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Trigger height on the control grid — 28 / 36 / 44px.' },
      { name: 'usePortal', type: 'boolean', default: 'true', description: 'The menu renders into the body at a fixed position aligned to the trigger, on the `popover` rung, and slides back inside the viewport rather than crossing its edge. It stays on screen: it flips to the side with room, caps itself at the room that side has (and scrolls), and hides when its trigger is scrolled out of view. Keyboard: Enter / Space / ArrowDown open with the first item focused, Escape and Tab close and return focus to the trigger. `false` is the inline menu.' },
      {
        name: 'menuVariant',
        type: "'default' | 'compact'",
        default: "'default'",
        description: 'Row density of the popover, independent of the trigger size — 36px rows or 28px. A roomy trigger over a long list is the usual reason to tighten it.',
      },
      { name: 'align', type: "'start' | 'end'", default: "'start'", description: 'Which trigger edge the menu lines up with — Popover\'s words. `end` for a control near the far edge of the page. `left` and `right` are the older spellings, still honoured.' },
      { name: 'icon', type: 'React.ReactNode', description: 'A glyph before the label.' },
      { name: 'showChevron', type: 'boolean', default: 'true', description: 'The affordance that says a list is coming. Hide it only when the icon already says so.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks the trigger.' },
      { name: 'className', type: 'string', description: 'Appended to the trigger.' },
      { name: 'menuMaxHeight', type: 'string', description: 'A CSS max-height after which the menu scrolls — for a long list of actions. Portalled, it never exceeds the room on screen either. Unset, the menu fits its options.' },
      { name: 'onClick', type: '(e: React.MouseEvent) => void', native: true, description: 'Native — everything not listed above, and the ref, reach the trigger button. A caller onClick runs first, and preventDefault() in it keeps the menu shut. A caller id becomes the trigger\'s id, and the menu is labelled by it.' },
      { name: 'data-testid', type: 'string', description: 'Applied to the trigger; options derive `${testid}-option-${id}`.' },
    ],
    snippet: `<DropdownButton
  label="Bulk actions"
  variant="subtle"
  options={[
    { id: 'export', label: 'Export selected', icon: <Download /> },
    { id: 'delete', label: 'Delete selected', disabled: !selection.length },
  ]}
  onSelect={runBulkAction}
  data-testid="products-bulk-actions"
/>`,
  },

  {
    name: 'SegmentedControl',
    section: 'segmented-control',
    importPath: 'Components/DesignSystem/SegmentedControl/SegmentedControl',
    typeFile: 'SegmentedControl.ts',
    summary:
      'A pick-one switch drawn as pills on a sunken track, with every option visible at once. Semantically a radio group: one tab stop, and arrow keys both move and select.',
    useWhen: [
      'Two to four short, mutually exclusive views — the All / Parent-Child / Individual shape.',
      'Seeing the alternatives is part of the decision.',
    ],
    insteadUse: [
      { component: 'Dropdown', when: 'there are more than four options, or the labels are long' },
      { component: 'Tab', when: 'the choice switches page content rather than filtering it' },
      { component: 'Toggle', when: 'it is one thing being on or off' },
    ],
    props: [
      { name: 'options', type: 'SegmentedControlOption[]', required: true, description: '`{ id, label, disabled?, data-testid? }`. Keep the labels to a word or two — they all share one track.' },
      { name: 'value', type: 'string | number', description: 'The selected option id. Controlled — omit it and pass `defaultValue` for a control that keeps its own selection. With nothing selected the first enabled segment holds the tab stop, so the control is always reachable, and the first arrow selects from wherever focus is.' },
      { name: 'defaultValue', type: 'string | number', description: 'Uncontrolled: the option selected first; the control then keeps its own selection. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(id: string | number) => void', description: 'Fires with the chosen id, from a click or an arrow key.' },
      { name: 'aria-label', type: 'string', native: true, description: 'The accessible name of the group. Always pass one — the pills say what they are, never what they switch.' },
      {
        name: 'ariaLabel',
        type: 'string',
        deprecated: 'Pass `aria-label` — the native spelling every component takes. It wins when both are passed.',
        description: 'Still honoured, so existing call sites need no change.',
      },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'sm'", description: 'The control rung — 28 / 36 / 44 with the track. `sm` is the height it always had (menu sections, editor rails); pass `md` beside md buttons in a toolbar. The label is `body-primary` at every rung.' },
      { name: 'fitContent', type: 'boolean', default: 'false', description: 'Segments hug their own label instead of splitting the track evenly — for options whose labels differ in length ("Production" / "Staging"). Off, every segment is the same width.' },
      { name: 'className', type: 'string', description: 'Appended to the track.' },
      { name: 'data-testid', type: 'string', description: 'Applied to the track; each pill derives `${testid}-option-${id}`.' },
    ],
    snippet: `<SegmentedControl
  aria-label="Compatibility scope"
  value={scope}
  onChange={setScope}
  options={[
    { id: 'all', label: 'All' },
    { id: 'parent-child', label: 'Parent-Child' },
    { id: 'individual', label: 'Individual' },
  ]}
  data-testid="compat-scope"
/>`,
  },

  /* ── Inputs ───────────────────────────────────────────────────────────────── */
  {
    name: 'TextField',
    section: 'text-fields',
    importPath: 'Components/DesignSystem/TextField/TextField',
    typeFile: 'TextField.ts',
    summary:
      'The single-line text input — a whole FIELD, not a bare input: label, helper text, error state, character count and optional copy button come with it.',
    useWhen: [
      'A form collects a short free-text or numeric value.',
      'The value needs a visible label, a validation state or a length limit.',
    ],
    insteadUse: [
      { component: 'Textarea', when: 'the value runs to sentences rather than a line' },
      { component: 'Dropdown', when: 'the value is one of a known, enumerable set' },
      { component: 'Search', when: 'typing filters a list rather than storing a value' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'The visible label, associated with the input. Always pass the real text; `hideLabel` hides it and keeps the name. An empty string leaves the field unnamed — the older opt-out, still honoured.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a field whose meaning is already on screen: a table cell, a filter bar, a row editor. Pass the real `label` with it.' },
      { name: 'value', type: 'string', default: "''", description: 'Controlled value. Omit it and pass `defaultValue` for a field that keeps what is typed.' },
      { name: 'defaultValue', type: 'string', description: 'Uncontrolled: the text the field starts with; it then keeps what is typed. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(value: string, event?) => void', description: 'Value first, event second — the shape every control in the set uses, so a handler is rarely more than a setter.' },
      { name: 'onBlur', type: '(event?) => void', description: 'Fires on blur. The usual place to validate a field the user has finished with.' },
      { name: 'placeholder', type: 'string', default: "''", description: 'Hint text. Never the label — it disappears the moment the user types.' },
      { name: 'type', type: "'text' | 'email' | 'password' | 'number'", default: "'text'", description: 'Native input type. `number` turns on the numeric guards below. The field shows its value on hover (`title`) only while the value overflows the box, and never on `password` — a password there is a tooltip and a description a screen reader speaks. A field with no label and no `aria-label` keeps its value as its `title`, because that is its only name: give it one.' },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Height on the shared control grid — 28 / 36 / 44px. Density only; the text stays one size.' },
      { name: 'labelSize', type: "'sm' | 'md'", default: "'md'", description: 'Label rung, for a dense config panel whose fields should not shout.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'Marks the label and sets the field required for assistive tech.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Paints the error state. Pair it with `helperText` — a red border that does not say what is wrong is half a message.' },
      { name: 'helperText', type: 'React.ReactNode', default: "''", description: 'The hint under the field. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      {
        name: 'additionalInfo',
        type: 'string',
        default: "''",
        deprecated: 'Put the sentence in `helperText`.',
        description: 'A second, quieter line under the hint. No call site passes it.',
      },
      { name: 'infoMessage', type: 'string', default: "''", description: 'Puts an info tooltip on the label for an explanation too long to sit inline.' },
      { name: 'maxLength', type: 'number', description: 'Caps the input and shows a count.' },
      { name: 'minValue', type: 'number', description: 'Numeric floor, for `type="number"`.' },
      { name: 'maxValue', type: 'number', description: 'Numeric ceiling, for `type="number"`.' },
      { name: 'decimalAllowed', type: 'boolean', default: 'true', description: 'Set false to reject a decimal point outright rather than rounding it later.' },
      { name: 'showCopyButton', type: 'boolean', default: 'false', description: 'Adds a copy action inside the field — for a generated key or an id the user needs elsewhere.' },
      { name: 'onCopy', type: '() => void', description: 'Fires after a copy, for a toast or a log.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks entry and drops the field to the disabled ink.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the field WRAPPER — label, control and helper together. Everything else reaches the input.' },
      { name: 'id', type: 'string', description: 'Native input id.' },
      { name: 'name', type: 'string', description: 'Native input name, which is what makes form-scoped queries work.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
      {
        name: 'ariaLabel',
        type: 'string',
        deprecated: 'Pass `aria-label` directly, or rely on `label`. `aria-label` wins when both are passed.',
        description: 'Applied even when a visible label renders, which lets the spoken name and the seen name drift apart — a hazard for voice-control users.',
      },
    ],
    snippet: `<TextField
  label="Part number"
  value={partNumber}
  onChange={setPartNumber}
  required
  error={!!errors.partNumber}
  helperText={errors.partNumber ?? 'As printed on the carton'}
  data-testid="product-part-number"
/>`,
  },

  {
    name: 'FieldTitle',
    section: 'text-fields',
    importPath: 'Components/DesignSystem/FieldTitle/FieldTitle',
    typeFile: 'FieldTitle.ts',
    summary:
      'The label row every field renders for itself — the text, the required marker and the optional info tooltip. You reach for it directly only when labelling something that is not a design-system field.',
    useWhen: [
      'You are labelling a hand-built or third-party control and want the app label, not a new one.',
      'A group of controls needs one heading above it rather than a label each.',
    ],
    insteadUse: [
      { component: 'TextField', when: 'you are labelling a text input — it renders this for you' },
      { component: 'Typography', when: 'the text is a heading rather than a name for a control' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'The label text.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'Renders the required marker after the text.' },
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: 'Label rung. `sm` for dense config panels.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Recedes the label with the control it names.' },
      { name: 'infoMessage', type: 'string', default: "''", description: 'Adds an info tooltip beside the text.' },
      {
        name: 'htmlFor',
        type: 'string',
        description:
          'Id of the control this labels, which makes clicking the label focus it. Only works on a labelable element — input, select, textarea. Pointing it at a button does nothing at all.',
      },
      {
        name: 'labelId',
        type: 'string',
        description:
          'Puts an id on the label TEXT alone, for a control that names itself with aria-labelledby — what a button-triggered control like Dropdown uses. Deliberately not on the whole row, or the name would swallow the required marker and the tooltip.',
      },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the row.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<FieldTitle
  label="Traffic split"
  required
  infoMessage="Shares must total 100%."
  htmlFor="traffic-split-input"
  data-testid="ab-traffic-split-label"
/>`,
  },

  {
    name: 'Dropdown',
    section: 'text-fields',
    importPath: 'Components/DesignSystem/Dropdown/Dropdown',
    typeFile: 'Dropdown.ts',
    summary:
      'The button-triggered select. The trigger shows the chosen option, the list owns the keyboard, and the whole thing is a field — label, helper text and error state included.',
    useWhen: [
      'The value is one of a set the user can read down.',
      'The list is long enough to want a filter box, or paged from the server.',
    ],
    insteadUse: [
      { component: 'Combobox', when: 'the user arrives holding a NAME and typing should filter, or the selection is multiple' },
      { component: 'SegmentedControl', when: 'there are two to four short options and seeing them all is part of the choice' },
      { component: 'DropdownButton', when: 'picking runs an action instead of storing a value' },
    ],
    props: [
      { name: 'debounceMs', type: 'number', description: 'Paces `onSearchChange` while the user types in the filter box. Pass `DEBOUNCE.search`; unset, every keystroke reports.' },
      { name: 'onClear', type: '() => void', description: 'Supply it and a clear × renders beside the chevron while a value is selected. The callback decides what cleared means — usually setting the value to undefined.' },
      { name: 'label', type: 'string', required: true, description: 'Visible label. Always pass the real text; `hideLabel` hides it and keeps the name. The trigger is named by the label AND the chosen option ("Scoring method Weighted"), so a screen reader hears what is selected; with nothing chosen, by the label alone. An empty string leaves the field unnamed — the older opt-out, still honoured.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a field whose meaning is already on screen: a table cell, a filter bar, a row editor. Pass the real `label` with it.' },
      {
        name: 'options',
        type: 'DropdownOption[]',
        required: true,
        description: '`{ value, label, disabled?, group?, icon?, tooltip? }`. Options sharing a `group` become one labelled group — order them contiguously, the grouping follows the array rather than sorting it.',
      },
      { name: 'value', type: 'string | number', description: 'The selected option value. Controlled — omit it and pass `defaultValue` for a field that keeps its own pick.' },
      { name: 'defaultValue', type: 'string | number', description: 'Uncontrolled: the option selected first; the field then keeps its own pick. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(value: string | number) => void', description: 'Fires with the chosen value. To have it TYPED, give the Dropdown the value type — `<Dropdown<Unit> …>` — and options, value and onChange must agree on it. Without the argument nothing is inferred: string | number, as ever.' },
      { name: 'onBlur', type: '() => void', description: 'Fires when the control loses focus.' },
      { name: 'placeholder', type: 'string', default: "'Select an option'", description: 'Trigger text before anything is chosen.' },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Trigger height on the control grid — 28 / 36 / 44px.' },
      { name: 'labelSize', type: "'sm' | 'md'", default: "'md'", description: 'Label rung.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'The asterisk on the label, and "Required" in the trigger\'s accessible description — not `aria-required`, which the button role does not allow.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Paints the error state.' },
      { name: 'helperText', type: 'React.ReactNode', default: "''", description: 'The hint under the field. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      { name: 'additionalInfo', type: 'string', default: "''", description: 'Quiet metadata beside the label.' },
      { name: 'infoMessage', type: 'string | React.ReactNode', default: "''", description: 'Info tooltip on the label. Takes a node when the explanation needs structure.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks the trigger. A trigger disabled while it holds focus (a page size that refetches on pick) keeps focus, aria-disabled until focus leaves.' },
      {
        name: 'searchable',
        type: 'boolean',
        default: 'false',
        description: 'Adds a filter box above the list, for a list long enough to scroll. It sits outside the listbox — an input is not a valid listbox child — names the list it filters (`aria-controls`), and speaks how many options are left once the query is still for half a second (`-status`). The query clears when the list closes. Without it, a letter typed on the closed trigger opens the list on the first option that starts with it, as a native select does.',
      },
      {
        name: 'onSearchChange',
        type: '(query: string) => void',
        description:
          'Server-side search. Supply it and the filter reports every keystroke instead of filtering locally — local filtering is SKIPPED, because re-filtering what the server already matched silently narrows a fuzzy or accent-insensitive match.',
      },
      { name: 'searchPlaceholder', type: 'string', default: "'Search'", description: 'Placeholder for the filter box.' },
      { name: 'onScrollEnd', type: '() => void', description: 'Fires near the bottom of the list so a paged caller can fetch the next slice. The caller owns the cursor and the end-of-list guard.' },
      { name: 'loading', type: 'boolean', default: 'false', description: 'Renders a loading row at the end of the list. Goes with `onScrollEnd`.' },
      {
        name: 'usePortal',
        type: 'boolean',
        default: 'true',
        description: 'The listbox renders into the body at a fixed position on the `popover` rung, so no dialog, side panel or scroll container can clip it. It stays on screen: it flips to the side with room, caps itself at the room that side has (and scrolls), and hides when its trigger is scrolled out of view. Tab inside the list returns focus to the trigger; `onBlur` does not fire when focus merely enters the list. `false` is the inline listbox.',
      },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the field wrapper; native attributes reach the trigger button.' },
      { name: 'renderOption', type: '(option, { selected, disabled }) => ReactNode', description: 'What an option row shows, in place of its icon, label and tooltip glyph — the SKU under a product name, a status beside it. The row stays the component\'s (role, selection ground, check, keyboard), so a custom option cannot break the list. Its text is the option\'s name: lead with the label. The trigger and the filter keep reading label.' },
      { name: 'data-testid', type: 'string', description: 'Applied to the trigger; options derive `${testid}-option-${value}`.' },
    ],
    snippet: `<Dropdown
  label="Target category"
  value={categoryId}
  onChange={setCategoryId}
  searchable
  usePortal
  options={categories.map((c) => ({
    value: c.id,
    label: c.name,
    group: c.type,
    disabled: c.id === sourceCategoryId,
  }))}
  data-testid="recategorize-target"
/>`,
  },

  {
    name: 'Textarea',
    section: 'textarea',
    importPath: 'Components/DesignSystem/Textarea/Textarea',
    typeFile: 'Textarea.ts',
    summary:
      'The multi-line field. Same chrome and same label, helper and error contract as TextField — it differs only in that the value is expected to run long.',
    useWhen: [
      'The value is a description, a note or anything the user writes in sentences.',
      'A length limit is worth showing as a count rather than enforcing silently.',
    ],
    insteadUse: [
      { component: 'TextField', when: 'the value is one line' },
      { component: 'Combobox', when: 'the long text is really a list of picked values' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'Visible label. Always pass the real text; `hideLabel` hides it and keeps the name. An empty string leaves the field unnamed — the older opt-out, still honoured.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a field whose meaning is already on screen: a table cell, a filter bar, a row editor. Pass the real `label` with it.' },
      { name: 'value', type: 'string', default: "''", description: 'Controlled value. Omit it and pass `defaultValue` for a field that keeps what is typed.' },
      { name: 'defaultValue', type: 'string', description: 'Uncontrolled: the text the field starts with; it then keeps what is typed. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(value: string, event?) => void', description: 'Value first, event second.' },
      { name: 'placeholder', type: 'string', default: "''", description: 'Hint text.' },
      { name: 'rows', type: 'number', default: '4', description: 'Native rows. The control also carries an 80px floor, so a value of 2 will not collapse it.' },
      {
        name: 'resize',
        type: "'vertical' | 'none'",
        default: "'vertical'",
        description: 'Horizontal resize is deliberately not offered: a field wider than its container breaks the form column, which is what the native `both` default does.',
      },
      { name: 'maxLength', type: 'number', description: 'Caps the value.' },
      { name: 'showCount', type: 'boolean', default: 'false', description: 'Renders an `n / max` counter. Needs `maxLength` to mean anything.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'Marks the label and the control.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Paints the error state.' },
      { name: 'helperText', type: 'React.ReactNode', default: "''", description: 'The hint under the field. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks entry.' },
      { name: 'labelSize', type: "'sm' | 'md'", default: "'md'", description: 'Label rung.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the field wrapper; the rest reaches the textarea.' },
      { name: 'id', type: 'string', description: 'Native id.' },
      { name: 'name', type: 'string', description: 'Native name.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Textarea
  label="Rule description"
  value={description}
  onChange={setDescription}
  rows={3}
  maxLength={280}
  showCount
  data-testid="rule-description"
/>`,
  },

  {
    name: 'Search',
    section: 'search',
    importPath: 'Components/DesignSystem/Search/Search',
    typeFile: 'Search.ts',
    summary:
      'The filter field: a magnifier, a clear button, and a busy state that says a query is running WITHOUT blocking the next keystroke.',
    useWhen: [
      'Typing narrows a list, a table or a set of options.',
      'A server-backed query is in flight and the user should see that the field caused it.',
    ],
    insteadUse: [
      { component: 'TextField', when: 'the text is a value the form stores' },
      { component: 'Combobox', when: 'typing should filter a list the user then picks ONE thing from' },
    ],
    props: [
      { name: 'debounceMs', type: 'number', description: '`onSearch` also fires this many ms after typing pauses; Enter and the clear × fire it at once and cancel the pending call. Pass `DEBOUNCE.search` (300) rather than a literal. Unset, `onSearch` fires on Enter and clear only, as before.' },
      { name: 'value', type: 'string', description: 'Controlled query. Omit it and pass `defaultValue` for a field that keeps what is typed.' },
      { name: 'defaultValue', type: 'string', description: 'Uncontrolled: the query the field starts with; it then keeps what is typed. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(value: string, event?) => void', description: 'Fires on every keystroke.' },
      { name: 'onSearch', type: '(value: string) => void', description: 'Fires on Enter, for a search that runs on submit rather than per keystroke.' },
      {
        name: 'label',
        type: 'string',
        description:
          'A visible label, tied to the input. Pass one whenever there is room: without it the field falls back to naming itself with the placeholder, and a placeholder vanishes the moment the user types — taking the accessible name with it.',
      },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a field whose meaning is already on screen: a table cell, a filter bar, a row editor. Pass the real `label` with it.' },
      { name: 'labelSize', type: "'sm' | 'md'", default: "'md'", description: 'Label rung, matching TextField.' },
      { name: 'placeholder', type: 'string', default: "'Search...'", description: 'Hint text, and the fallback accessible name.' },
      {
        name: 'loading',
        type: 'boolean',
        default: 'false',
        description:
          'The field is working: the magnifier becomes a spinner and aria-busy is set. It blocks NOTHING — unlike a submit button, a search field is typed through, and each keystroke starts the next query.',
      },
      {
        name: 'loadingLabel',
        type: 'string',
        default: "'Searching'",
        description: 'What the spinner announces. Narrow it when a page runs more than one at once — "Searching products".',
      },
      { name: 'clearable', type: 'boolean', default: 'true', description: 'Shows the clear button once there is a query.' },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Density, not type size — all three render 14px text and differ only in padding.' },
      { name: 'searchIconPosition', type: "'start' | 'end'", default: "'start'", description: 'Which end the magnifier sits at.' },
      { name: 'shortcut', type: 'string', description: 'The key that focuses this field, as keycaps at the trailing edge while the field is empty and enabled — and as `aria-keyshortcuts` on the input, so the ARIA spelling (`"Meta+K"`) is the one source. Binding the key is yours.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Paints the error state.' },
      { name: 'helperText', type: 'React.ReactNode', description: 'The hint under the field. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks entry.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the outer column; the rest reaches the input.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
      { name: 'aria-label', type: 'string', native: true, description: 'Accessible name, when there is no `label`. Without either, the placeholder names the field — prefer a real `label`.' },
      {
        name: 'ariaLabel',
        type: 'string',
        deprecated: 'Pass `aria-label` — the native spelling every component takes. It wins when both are passed.',
        description: 'Still honoured, so existing call sites need no change.',
      },
      {
        name: 'variant',
        type: "'default' | 'bordered'",
        default: "'default'",
        deprecated: 'Both values render identically. Do not reach for it.',
        description: 'Once meant a lighter border on white versus a heavier one on a tint; the tint was removed because placeholder ink on it measured under AA.',
      },
    ],
    snippet: `<Search
  label="Find a product"
  value={query}
  onChange={setQuery}
  loading={isFetching}
  loadingLabel="Searching products"
  data-testid="products-search"
/>`,
  },

  {
    name: 'Combobox',
    section: 'combobox',
    importPath: 'Components/DesignSystem/Combobox/Combobox',
    typeFile: 'Combobox.ts',
    summary:
      'The typeahead select. Its trigger IS a text input: typing filters as you type, focus never leaves the field, and arrow keys move a virtual highlight — so you can keep typing mid-navigation.',
    useWhen: [
      'The user arrives holding a NAME — a product, an attribute, a category — rather than browsing a list.',
      'The list is long or server-backed, or the selection is multiple.',
    ],
    insteadUse: [
      { component: 'Dropdown', when: 'the list is short and enumerable, and browsing beats typing' },
      { component: 'Search', when: 'typing filters a view rather than choosing a value' },
    ],
    props: [
      { name: 'debounceMs', type: 'number', description: 'Paces `onSearch` while the user types. Pass `DEBOUNCE.search`; the resets (a pick, a close, a clear) still reach the server at once.' },
      { name: 'clearable', type: 'boolean', default: 'false', description: 'A clear × while something is chosen — single calls `onChange(null, null)`, multi `onValuesChange([])`. Backspace on an empty query clears a single value too, so the keyboard needs no second tab stop.' },
      { name: 'label', type: 'string', required: true, description: 'Visible label. Always pass the real text; `hideLabel` hides it and keeps the name. An empty string leaves the field unnamed — the older opt-out, still honoured.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a field whose meaning is already on screen: a table cell, a filter bar, a row editor. Pass the real `label` with it.' },
      {
        name: 'options',
        type: 'ComboboxOption[]',
        required: true,
        description: '`{ value, label, disabled? }`. With `onSearch` these are the server answer and render as-is; without it they are the whole universe and the query filters them locally on the label.',
      },
      { name: 'value', type: 'string | null', default: 'null', description: 'Single-select value. Ignored under `multiple`. Omit it and pass `defaultValue` for a field that keeps its own pick.' },
      { name: 'defaultValue', type: 'string | null', description: 'Uncontrolled: the single-select value it starts with; it then keeps its own pick. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(value: string | null, option: ComboboxOption | null) => void', description: 'Single-select change. Hands back the option too, for callers that need the label.' },
      {
        name: 'multiple',
        type: 'boolean',
        default: 'false',
        description: 'Multi-select: values render as removable chips in the field, picking keeps the list open, picking a chosen option again removes it, and Backspace in an empty query removes the last chip.',
      },
      { name: 'values', type: 'string[]', default: '[]', description: 'The chosen values, under `multiple`. Omit it and pass `defaultValues` for a field that keeps its own.' },
      { name: 'defaultValues', type: 'string[]', description: 'Uncontrolled: the values chosen at first, under `multiple`; the field then keeps its own. Ignored while the controlled prop is passed.' },
      { name: 'onValuesChange', type: '(values: string[]) => void', description: 'Multi-select change — the full next array, ready for a state setter.' },
      {
        name: 'onSearch',
        type: '(query: string) => void',
        description: 'Server-side search. Local filtering is SKIPPED while it is set, because re-filtering a list the server already matched silently narrows it.',
      },
      { name: 'loading', type: 'boolean', default: 'false', description: 'Renders the loading row in the open list.' },
      { name: 'placeholder', type: 'string', default: "'Search…'", description: 'Hint text in the input.' },
      { name: 'noResultsText', type: 'string', default: "'No matching options'", description: 'Copy for the empty-filter row.' },
      { name: 'resultsAnnouncement', type: '(count: number) => string', default: 'announceResultCount', description: 'What a screen reader hears once a typed query has been still for half a second: how many options it left — by default "3 options available", "1 option available" or "No options available". Spoken through a hidden status region mounted with the field (`${testid}-status`), never while loading and never over the typing. A creatable field adds its create row\'s words.' },
      { name: 'usePortal', type: 'boolean', default: 'true', description: 'The list renders into the body at a fixed position sized to the field, on the `popover` rung, so a table or dialog body cannot clip it. It stays on screen: it flips to the side with room, caps itself at the room that side has (and scrolls), and hides when its trigger is scrolled out of view. `false` is the inline list.' },
      { name: 'creatable', type: 'boolean', default: 'false', description: 'A "Create …" row at the end of the list while the typed query matches no option exactly. The caller adds the option and selects it.' },
      { name: 'onCreate', type: '(query: string) => void', description: 'Fires with the trimmed query when the create row is chosen. Single closes; multi keeps the list open.' },
      { name: 'createLabel', type: '(query: string) => string', default: '`Create "${query}"`', description: 'The create row\'s copy.' },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'The field MINIMUM height — multi-select wraps taller as chips accumulate.' },
      { name: 'labelSize', type: "'sm' | 'md'", default: "'md'", description: 'Label rung.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'Marks the label and the control.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Paints the error state.' },
      { name: 'helperText', type: 'React.ReactNode', default: "''", description: 'The hint under the field. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks entry.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the outer wrapper, like every field.' },
      { name: 'id', type: 'string', description: 'Native id.' },
      { name: 'name', type: 'string', description: 'Native name.' },
      { name: 'renderOption', type: '(option, { selected, active, disabled }) => ReactNode', description: 'What an option row shows, in place of its label. The row stays the component\'s, and `active` says which row the keyboard is on. Its text is the option\'s name: lead with the label. Chips, the input and the filter keep reading label.' },
      {
        name: 'data-testid',
        type: 'string',
        description: 'Templates the whole inventory: `-field`, `-toggle`, `-list`, `-option-${value}`, `-tag-${value}`, `-no-options`, `-loading`.',
      },
    ],
    snippet: `<Combobox
  label="Compatible parts"
  multiple
  values={selectedParts}
  onValuesChange={setSelectedParts}
  options={partResults}
  onSearch={fetchParts}
  loading={isFetchingParts}
  data-testid="rule-compatible-parts"
/>`,
  },

  {
    name: 'NumberField',
    section: 'text-fields',
    importPath: 'Components/DesignSystem/NumberField/NumberField',
    typeFile: 'NumberField.ts',
    summary:
      'The numeric input: a text field wearing role="spinbutton" whose value is always a number or null — arrows step, Shift steps by ten, Home/End reach the bounds, and blur clamps and rounds.',
    useWhen: [
      'The value is a quantity, a weight, a percentage, a price — anything you would do arithmetic on.',
      'You were about to write <input type="number"> and style its spinner.',
    ],
    insteadUse: [
      { component: 'TextField', when: 'the digits are an identifier — a SKU, a postcode, a phone number — that is never added up' },
      { component: 'Slider', when: 'the range is small and the rough position matters more than the exact figure' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'The visible label, associated through htmlFor. Always pass the real text; `hideLabel` hides it and keeps the name. An empty string leaves the field unnamed — the older opt-out, still honoured.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a field whose meaning is already on screen: a table cell, a filter bar, a row editor. Pass the real `label` with it.' },
      { name: 'value', type: 'number | null', description: 'The value; null is the empty field. Controlled — omit it and pass `defaultValue` for a field that keeps its own number.' },
      { name: 'defaultValue', type: 'number | null', description: 'Uncontrolled: the number the field starts with; it then keeps its own. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(value: number | null) => void', description: 'Every keystroke that parses to a number, unclamped; then once more on blur if clamping or rounding changed it. null when emptied. A complete grouping in the user\'s locale reads as that number — "1,000" is 1000 in en-US — and a lone `.` or `,` is the decimal point.' },
      { name: 'min', type: 'number', description: 'Lower bound — clamped on blur and by the steppers; Home jumps to it.' },
      { name: 'max', type: 'number', description: 'Upper bound — clamped on blur and by the steppers; End jumps to it.' },
      { name: 'step', type: 'number', default: '1', description: 'The arrow / button increment. Shift, PageUp and PageDown step by ten of these.' },
      { name: 'precision', type: 'number', description: 'Decimal places the value is rounded to. Defaults to the decimals in step.' },
      { name: 'unit', type: 'string', description: 'A suffix inside the field — "kg", "%", "pcs" — also read out in aria-valuetext.' },
      { name: 'steppers', type: 'boolean', default: 'true', description: 'The − / + buttons. Mouse-only, outside the tab order. Off for a dense table cell.' },
      { name: 'placeholder', type: 'string', default: "''", description: 'Shown while the field is empty.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'The sunken, un-hoverable field.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'The asterisk on the label and aria-required on the input.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Danger border and ring; helperText announces.' },
      { name: 'helperText', type: 'React.ReactNode', description: 'The hint under the field. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Height on the shared control grid.' },
      { name: 'labelSize', type: "'sm' | 'md'", description: 'The label\'s type rung.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the field wrapper; native attributes and the ref reach the input.' },
      { name: 'id', type: 'string', description: 'Native input id.' },
      { name: 'name', type: 'string', description: 'Native input name.' },
      { name: 'data-testid', type: 'string', description: 'The input; the row is -field, then -unit, -decrement, -increment.' },
    ],
    snippet: `<NumberField
  label="Quantity"
  value={quantity}
  onChange={setQuantity}
  min={0}
  max={999}
  unit="pcs"
  data-testid="line-quantity"
/>`,
  },

  {
    name: 'ConfirmDialog',
    section: 'overlays',
    importPath: 'Components/DesignSystem/ConfirmDialog/ConfirmDialog',
    typeFile: 'ConfirmDialog.ts',
    summary:
      'The two-button question over Dialog: Cancel, Escape and the scrim are one route, both routes are refused while loading, and a danger tone fills the confirm red.',
    useWhen: [
      'An action needs a yes before it runs — a delete, a promote, discarding a draft.',
      'You were about to compose a Dialog with a footer of two Buttons and a loading flag.',
    ],
    insteadUse: [
      { component: 'Dialog', when: 'the surface holds a form or anything beyond a question and two answers' },
      { component: 'Toast', when: 'the action already ran and the user only needs to know' },
    ],
    props: [
      { name: 'open', type: 'boolean', default: 'false', description: 'Controlled visibility. The one name every overlay takes.' },
      {
        name: 'isOpen',
        type: 'boolean',
        deprecated: 'Pass `open` — the name every overlay in the library takes. `open` wins when both are passed.',
        description: 'Still honoured, so existing call sites need no change.',
      },
      { name: 'title', type: 'string', required: true, description: 'The question, as a heading — "Delete this domain?"' },
      { name: 'message', type: 'React.ReactNode', required: true, description: 'The consequence. A node, so part of it can be emphasised.' },
      { name: 'secondaryMessage', type: 'React.ReactNode', description: 'A second paragraph — "This cannot be undone."' },
      { name: 'confirmText', type: 'string', default: "'Confirm'", description: 'Name the action — "Delete domain", never "Yes".' },
      { name: 'cancelText', type: 'string', default: "'Cancel'", description: 'The way out.' },
      { name: 'tone', type: "'danger' | 'warning'", description: 'danger fills the confirm red and puts the glyph beside the title; omit it for a decision that is not a deletion.' },
      { name: 'loading', type: 'boolean', default: 'false', description: 'The request is in flight: the confirm spins, Cancel and Escape are refused.' },
      { name: 'busyText', type: 'string', description: 'The confirm\'s label while loading — "Deleting…". Defaults to confirmText.' },
      { name: 'onConfirm', type: '() => void', required: true, description: 'The confirm button.' },
      { name: 'onCancel', type: '() => void', required: true, description: 'Cancel, Escape and (when enabled) the scrim. The dialog opens as an alert dialog, described by its message, with focus on Cancel — the least destructive answer — so Enter never confirms by accident.' },
      { name: 'size', type: "'sm' | 'md' | 'lg' | 'xl' | 'half'", default: "'sm'", description: 'Dialog\'s sizes. A question is short.' },
      { name: 'closeOnOutsideClick', type: 'boolean', default: 'false', description: 'As on Dialog — a stray click should not answer a question.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the body wrapper.' },
      { name: 'data-testid', type: 'string', description: 'The dialog; then -message, -secondary-message, -confirm, -cancel, plus Dialog\'s own -title / -footer.' },
    ],
    snippet: `<ConfirmDialog
  open={pendingDelete !== null}
  title="Delete this domain?"
  tone="danger"
  message={<>Every channel under <strong>{pendingDelete?.host}</strong> loses its mapping.</>}
  secondaryMessage="This cannot be undone."
  confirmText="Delete domain"
  busyText="Deleting…"
  loading={deleting}
  onConfirm={deleteDomain}
  onCancel={() => setPendingDelete(null)}
  data-testid="domain-delete-confirm"
/>`,
  },

  {
    name: 'CommandPalette',
    section: 'command-palette',
    importPath: 'Components/DesignSystem/CommandPalette/CommandPalette',
    typeFile: 'CommandPalette.ts',
    summary:
      'The ⌘K surface: a modal search over a list of commands, ranked as you type, run with Enter. Controlled — the app shell binds the key that opens it.',
    useWhen: [
      'The app has more destinations and actions than a menu can hold and the user knows the name of what they want.',
      'A keyboard user should reach any screen without leaving the keyboard.',
    ],
    insteadUse: [
      { component: 'Menu', when: 'the actions belong to one thing — a row, a button — and there are fewer than a dozen' },
      { component: 'Search', when: 'the user is searching DATA (products, rules), not commands' },
      { component: 'Combobox', when: 'the pick is a form value, not an action' },
    ],
    props: [
      { name: 'open', type: 'boolean', required: true, description: 'Controlled visibility. Bind Meta+K (Control+K off a mac) in the shell and flip this.' },
      { name: 'onOpenChange', type: '(open: boolean) => void', required: true, description: 'Escape, the scrim and a run all ask to close through this.' },
      { name: 'commands', type: 'CommandPaletteCommand[]', required: true, description: '{ id, label, description?, icon?, shortcut?, group?, keywords?, disabled?, onSelect? } — group names render as headings while browsing; a query flattens the list into rank order.' },
      { name: 'onSelect', type: '(command: CommandPaletteCommand) => void', description: 'Fires after the command\'s own onSelect, with the command.' },
      { name: 'placeholder', type: 'string', default: "'Type a command or search…'", description: 'The input\'s placeholder.' },
      { name: 'emptyText', type: 'string', default: "'No matching commands'", description: 'The row shown when nothing matches.' },
      { name: 'aria-label', type: 'string', native: true, default: "'Command palette'", description: 'The dialog\'s and the input\'s accessible name.' },
      {
        name: 'ariaLabel',
        type: 'string',
        deprecated: 'Pass `aria-label` — the native spelling every component takes. It wins when both are passed.',
        description: 'Still honoured, so existing call sites need no change.',
      },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the panel.' },
      { name: 'data-testid', type: 'string', description: 'The panel; then -input, -list, -option-<id>, -group-<slug>, -empty, -backdrop.' },
    ],
    snippet: `<CommandPalette
  open={paletteOpen}
  onOpenChange={setPaletteOpen}
  commands={[
    { id: 'go-products', label: 'Go to Products', group: 'Navigate', onSelect: () => navigate('/gms/products') },
    { id: 'new-product', label: 'Create product', group: 'Actions', shortcut: 'Meta+N', onSelect: openCreate },
  ]}
  data-testid="command-palette"
/>`,
  },

  {
    name: 'RadioGroup',
    section: 'choice-controls',
    importPath: 'Components/DesignSystem/RadioGroup/RadioGroup',
    typeFile: 'RadioGroup.ts',
    summary:
      'The radios as a FIELD: one shared name (so arrow keys move between them and the group is one tab stop), the group role, a label, requiredness and the error line — rendered from an options array like Dropdown.',
    useWhen: [
      'The user picks exactly one of a few named options and the choice is part of a form.',
      'You were about to wrap bare radios in a div with role="radiogroup" and hand each a name.',
    ],
    insteadUse: [
      { component: 'Radio', when: 'the layout is not a list — a radio per table row, or each option carries its own controls' },
      { component: 'SegmentedControl', when: 'the options are two to four short views and the switch should read as one control' },
      { component: 'Dropdown', when: 'there are more than five or six options' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'The visible group label, naming the group through aria-labelledby. Always pass the real text; `hideLabel` hides it and keeps the name. An empty string opts out — then give the group an aria-label.' },
      { name: 'options', type: 'RadioGroupOption[]', required: true, description: '`{ value, label, disabled? }` — label is a node when part of the sentence needs emphasis.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a field whose meaning is already on screen: a table cell, a filter bar, a row editor. Pass the real `label` with it.' },
      { name: 'value', type: 'string | number', description: 'The selected value. Controlled — omit it and pass `defaultValue` for a group that keeps its own choice.' },
      { name: 'defaultValue', type: 'string | number', description: 'Uncontrolled: the option chosen first; the group then keeps its own choice. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(value: string | number) => void', description: 'Fires with the option the user picked.' },
      { name: 'name', type: 'string', description: 'The shared native name. Defaults to a generated one; pass it for form submission.' },
      { name: 'orientation', type: "'vertical' | 'horizontal'", default: "'vertical'", description: 'Stacked, or a wrapping row for two or three short options.' },
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: 'Forwarded to every radio — the same two rungs Checkbox and Radio share.' },
      { name: 'labelSize', type: "'sm' | 'md'", default: "'md'", description: 'The group label\'s rung, for a dense config panel whose fields should not shout.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'The asterisk on the label and aria-required on the group.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Paints helperText in the danger ink and announces it.' },
      { name: 'helperText', type: 'React.ReactNode', description: 'The hint under the group. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Disables every option; a single option disables itself in options.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the field wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Lands on the group; options template `-option-<value>`.' },
    ],
    snippet: `<RadioGroup
  label="Promotion"
  required
  value={promotion}
  onChange={setPromotion}
  options={[
    { value: 'immediate', label: 'Promote immediately' },
    { value: 'scheduled', label: 'Schedule for later' },
  ]}
  error={!promotion}
  helperText="Choose one to continue"
  data-testid="promote-mode"
/>`,
  },

  {
    name: 'CheckboxGroup',
    section: 'choice-controls',
    importPath: 'Components/DesignSystem/CheckboxGroup/CheckboxGroup',
    typeFile: 'CheckboxGroup.ts',
    summary:
      'Several independent choices as ONE field: the group role named by its label, the hint and the error line, requiredness, an optional select-all, and the ticked values as one array.',
    useWhen: [
      'The user may tick any number of a few named options, and the choice belongs to a form or a filter.',
      'You were about to map an array to bare Checkboxes under a heading nothing connects them to.',
    ],
    insteadUse: [
      { component: 'Checkbox', when: 'there is one box ("I agree"), or a box per table row' },
      { component: 'RadioGroup', when: 'exactly one option may be picked' },
      { component: 'Combobox', when: 'there are many options and the user should search them' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'The visible group label, naming the group through aria-labelledby. Always pass the real text; `hideLabel` hides it and keeps the name. An empty string opts out — then give the group an aria-label.' },
      { name: 'options', type: 'CheckboxGroupOption[]', required: true, description: '`{ value, label, disabled? }` — label is a node when part of the sentence needs emphasis.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a filter bar or a row editor whose meaning is already on screen. Pass the real `label` with it.' },
      { name: 'value', type: 'Array<string | number>', description: 'The ticked values. Controlled — omit it and pass `defaultValue` for a group that keeps its own selection. Reported back in options order, and a value the options do not list is kept, so a list filtered by a search box cannot drop a selection it is hiding.' },
      { name: 'defaultValue', type: 'Array<string | number>', description: 'Uncontrolled: the values ticked first; the group then keeps its own selection. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(value: Array<string | number>) => void', description: 'Fires with the whole new selection.' },
      { name: 'selectAllLabel', type: 'string', description: 'Adds a tri-state box above the options, with this label: ticked when every enabled option is, mixed when some are. It never changes a disabled option.' },
      { name: 'name', type: 'string', description: 'The native name every box shares, for form submission; each box submits its own value.' },
      { name: 'orientation', type: "'vertical' | 'horizontal'", default: "'vertical'", description: 'Stacked, or a wrapping row for two or three short options.' },
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: 'Forwarded to every box — the same two rungs Checkbox and Radio share.' },
      { name: 'labelSize', type: "'sm' | 'md'", default: "'md'", description: 'The group label\'s rung, for a dense panel whose fields should not shout.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'At least one box must be ticked: the asterisk on the label, and the group is described as required, because ARIA does not allow aria-required on a group.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Paints helperText in the danger ink and announces it.' },
      { name: 'helperText', type: 'React.ReactNode', description: 'The hint under the group. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Disables every box; a single option disables itself in options.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the field wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Lands on the group; options template `-option-<value>`, the select-all box `-select-all`, the hidden required word `-required`.' },
    ],
    snippet: `<CheckboxGroup
  label="Materials"
  selectAllLabel="All materials"
  value={materials}
  onChange={setMaterials}
  options={[
    { value: 'steel', label: 'Stainless steel' },
    { value: 'brass', label: 'Brass' },
    { value: 'nylon', label: 'Nylon' },
  ]}
  data-testid="part-materials"
/>`,
  },

  {
    name: 'Toggle',
    section: 'choice-controls',
    importPath: 'Components/DesignSystem/Toggle/Toggle',
    typeFile: 'Toggle.ts',
    summary:
      'The switch. One setting, on or off, taking effect as soon as it is flipped — no Save between the gesture and the consequence.',
    useWhen: [
      'A single setting turns something on or off and applies immediately.',
      'The two states are opposites that need no naming beyond the label.',
    ],
    insteadUse: [
      { component: 'Checkbox', when: 'the value is submitted with a form, or the user is ticking items in a list' },
      { component: 'SegmentedControl', when: 'there are more than two states, or the states need names' },
    ],
    props: [
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: '`sm` is the 36×20 switch for a dense config row or a table cell; `md` everywhere else, exactly as it always rendered. Density only — the label keeps its type.' },
      { name: 'label', type: 'string', required: true, description: 'The visible label, and the accessible name in both placements. Clicking it flips the switch, as a Checkbox label ticks its box.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the switch\'s name — for a switch in a table cell whose column already says what it switches.' },
      { name: 'checked', type: 'boolean', description: 'Controlled state. Omit it and pass `defaultChecked` for a switch that keeps its own.' },
      { name: 'defaultChecked', type: 'boolean', description: 'Uncontrolled: the state the switch starts in; it then keeps its own. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(checked: boolean, event?) => void', description: 'Fires with the NEXT state, so a handler is usually just a setter.' },
      {
        name: 'labelPlacement',
        type: "'end' | 'top'",
        default: "'end'",
        description: 'Layout only — the name is announced the same either way. `top` stacks the label above the switch, for a row of captioned toggles that would be three times as wide inline.',
      },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks the switch.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the ROW wrapper, not the switch — it is a layout hook.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Toggle
  label="Publish to storefront"
  checked={isPublished}
  onChange={setIsPublished}
  data-testid="model-publish-toggle"
/>`,
  },

  {
    name: 'Checkbox',
    section: 'choice-controls',
    importPath: 'Components/DesignSystem/Checkbox/Checkbox',
    typeFile: 'Checkbox.ts',
    summary:
      'The tick box — a native input inside its own label, so the whole row is the hit target. It is the only one of the three that has a third state.',
    useWhen: [
      'The user picks any number of items from a list, or agrees to one thing in a form.',
      'A parent row needs to show that only some of its children are selected.',
    ],
    insteadUse: [
      { component: 'Toggle', when: 'the setting applies the instant it changes' },
      { component: 'Radio', when: 'the options are mutually exclusive' },
    ],
    props: [
      {
        name: 'label',
        type: 'React.ReactNode',
        description: 'Text, or a node when part of the sentence needs emphasis. Never put a control inside it — the input already lives in the label.',
      },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the box\'s name — for a row checkbox whose row already says what it selects.' },
      { name: 'checked', type: 'boolean', description: 'Controlled state. Omit it and pass `defaultChecked` for a box that keeps its own.' },
      { name: 'defaultChecked', type: 'boolean', description: 'Uncontrolled: the state the box starts in; it then keeps its own. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(checked: boolean, event?) => void', description: 'Fires with the next state.' },
      {
        name: 'indeterminate',
        type: 'boolean',
        default: 'false',
        description: 'The third state: some children selected, not all. Visual and announced only — `checked` still decides what a click does next.',
      },
      {
        name: 'labelClamp',
        type: 'boolean',
        default: 'true',
        description: 'Clamps a long label to two lines, which is what a table row or filter list wants. Pass false when the extra lines ARE the content — clamping eats the half of the sentence that says what the option does.',
      },
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: '`sm` for a config panel or a table row, `md` everywhere else.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks the box.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the label wrapper; the rest reaches the input.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Checkbox
  label="Include discontinued parts"
  checked={includeDiscontinued}
  onChange={setIncludeDiscontinued}
  size="sm"
  data-testid="filters-include-discontinued"
/>`,
  },

  {
    name: 'Radio',
    section: 'choice-controls',
    importPath: 'Components/DesignSystem/Radio/Radio',
    typeFile: 'Radio.ts',
    summary:
      'One option in a mutually exclusive set. Give every member the same `name` and the group becomes one tab stop with arrow-key navigation, the way a radio group is supposed to behave.',
    useWhen: [
      'Exactly one of several options applies, and the user should see them all.',
      'An option needs a sentence of explanation that must stay clickable with it.',
    ],
    insteadUse: [
      { component: 'Dropdown', when: 'there are more options than fit comfortably on screen' },
      { component: 'SegmentedControl', when: 'the choice switches a view rather than setting a value' },
      { component: 'Checkbox', when: 'more than one may apply' },
    ],
    props: [
      {
        name: 'label',
        type: 'React.ReactNode',
        required: true,
        description: 'A node, not just a string, so an explanation stays INSIDE the label — otherwise clicking the explanation fails to select the option it explains.',
      },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the option\'s name.' },
      { name: 'value', type: 'string | number', required: true, description: 'This option value.' },
      { name: 'checked', type: 'boolean', required: true, description: 'Whether this option is the chosen one.' },
      { name: 'onChange', type: '(value: string | number, event?) => void', required: true, description: 'Fires with this option value.' },
      {
        name: 'name',
        type: 'string',
        description: 'The group name. Pass the SAME value to every member — that is what gives the set one tab stop and arrow-key movement instead of one stop per option.',
      },
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: 'Matches Checkbox rung for rung, so a form can be dense without its radios disagreeing with its boxes.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks this option.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the label wrapper; the rest reaches the input.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `{MATCH_MODES.map((mode) => (
  <Radio
    key={mode.value}
    name="match-mode"
    value={mode.value}
    label={mode.label}
    checked={matchMode === mode.value}
    onChange={setMatchMode}
    data-testid={\`match-mode-\${mode.value}\`}
  />
))}`,
  },

  {
    name: 'Slider',
    section: 'slider',
    importPath: 'Components/DesignSystem/Slider/Slider',
    typeFile: 'Slider.ts',
    summary:
      'The range input, as a labelled field. Best where the approximate position matters more than the exact number — a weight, a share, a threshold.',
    useWhen: [
      'The value is bounded and the user is tuning it by feel.',
      'Seeing where a value sits between its limits is part of understanding it.',
    ],
    insteadUse: [
      { component: 'TextField', when: 'the exact number matters and the user knows it' },
      { component: 'SegmentedControl', when: 'there are really only a few positions' },
    ],
    props: [
      {
        name: 'label',
        type: 'string',
        required: true,
        description: 'Required on purpose. The control this replaced announced only "slider, 33" — with four traffic sliders on one screen, nothing said WHICH. A caller with nothing to say here needs to notice that.',
      },
      {
        name: 'hideLabel',
        type: 'boolean',
        default: 'false',
        description: 'Keeps the accessible name, drops the visible one — for a slider in a grid whose column caption is rendered once above it.',
      },
      { name: 'value', type: 'number', description: 'Controlled value. Omit it and pass `defaultValue` for a slider that keeps its own.' },
      { name: 'defaultValue', type: 'number', description: 'Uncontrolled: the value the slider starts at; it then keeps its own. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(value: number, event?) => void', description: 'Value first, already parsed as a number — fractional steps survive.' },
      { name: 'min', type: 'number', default: '0', description: 'Floor. Zero, not one: a range input clamps below-min values, so a floor of 1 makes a slider announce 1 while the readout beside it says 0.' },
      { name: 'max', type: 'number', default: '100', description: 'Ceiling.' },
      { name: 'step', type: 'number', default: '1', description: 'Granularity. Say so here if you want integers.' },
      { name: 'showValue', type: 'boolean', default: 'false', description: 'Renders the current value at the end of the label row.' },
      {
        name: 'formatValue',
        type: '(value: number) => string',
        description: 'Formats the readout AND what is announced. Without it a percentage announces "33" — the unit, which is what makes the number mean anything, lives only in a sibling element the announcement never reaches.',
      },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Track and thumb size. Density only.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks the track. It stays readable as a position rather than fading out.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper — label row plus track.' },
      { name: 'data-testid', type: 'string', description: 'Forwarded to the range input. Always pass one per instance: two sliders rendered as siblings both answering to one hook is exactly the bug this component was built to end.' },
    ],
    snippet: `<Slider
  label="Attribute weight"
  value={weight}
  onChange={setWeight}
  min={0}
  max={100}
  step={5}
  showValue
  formatValue={(v) => \`\${v}%\`}
  data-testid="relevancy-attribute-weight"
/>`,
  },

  {
    name: 'DatePicker',
    section: 'date-time',
    importPath: 'Components/DesignSystem/DatePicker/DatePicker',
    typeFile: 'DatePicker.ts',
    summary:
      'The date field and its calendar. It hands back both the Date and its `YYYY-MM-DD` rendering, so no caller has to write the zero-padding formatter again.',
    useWhen: [
      'A form collects one calendar date.',
      'A range needs bounding — a start no earlier than today, an end no later than a deadline.',
    ],
    insteadUse: [
      { component: 'TimePicker', when: 'the value is a time of day' },
      { component: 'TextField', when: 'the value is a date-shaped string you do not want a calendar for' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'Visible label. Always pass the real text; `hideLabel` hides it and keeps the name. An empty string leaves the field unnamed — the older opt-out, still honoured.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a field whose meaning is already on screen: a table cell, a filter bar, a row editor. Pass the real `label` with it.' },
      {
        name: 'value',
        type: 'Date | IsoDateString | null',
        default: 'null',
        description: 'A string is parsed as `YYYY-MM-DD` in LOCAL time. Parsing it the native way reads it as UTC midnight, which highlights the previous day anywhere west of Greenwich.',
      },
      { name: 'defaultValue', type: 'Date | IsoDateString | null', description: 'Uncontrolled: the date the field starts with; it then keeps its own. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(date: Date | null, formatted: IsoDateString) => void', description: 'Receives the Date and its ISO string. Both are empty when cleared.' },
      { name: 'placeholder', type: 'string', default: "'dd-mm-yyyy'", description: 'Hint text in the field.' },
      { name: 'minDate', type: 'Date', description: 'Days before this are unselectable. The only supported way to gate past days.' },
      { name: 'maxDate', type: 'Date', description: 'Days after this are unselectable.' },
      { name: 'dateFormat', type: 'string', default: "'dd-MM-yyyy'", description: 'How the FIELD text reads. The `onChange` string is always ISO regardless.' },
      {
        name: 'open',
        type: 'boolean',
        description: 'Controlled open state, for the pattern where a separate button reveals the calendar. Omit it and the calendar manages itself on focus and click.',
      },
      { name: 'onOpenChange', type: '(open: boolean) => void', description: 'Pairs with `open`.' },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Field height on the control grid.' },
      { name: 'labelSize', type: "'sm' | 'md'", default: "'md'", description: 'Label rung.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'Marks the label and the control.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Paints the error state.' },
      { name: 'helperText', type: 'React.ReactNode', default: "''", description: 'The hint under the field. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks the field and the calendar together.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the field wrapper.' },
      { name: 'id', type: 'string', description: 'Native id, and what the label points at.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook; the calendar gets `${testid}-calendar`.' },
    ],
    snippet: `<DatePicker
  label="Schedule start"
  value={startDate}
  onChange={(date, iso) => setStart(iso)}
  minDate={new Date()}
  required
  data-testid="schedule-start-date"
/>`,
  },

  {
    name: 'TimePicker',
    section: 'date-time',
    importPath: 'Components/DesignSystem/TimePicker/TimePicker',
    typeFile: 'TimePicker.ts',
    summary:
      'The time field and its picker. You read and type it in 12-hour with AM/PM; it always stores 24-hour `HH:mm`, and shows you the stored value so the two can never disagree.',
    useWhen: [
      'A form collects a time of day — a schedule, a cutoff, a window.',
      'The stored value must be unambiguous no matter whose locale is reading it.',
    ],
    insteadUse: [
      { component: 'DatePicker', when: 'the value is a calendar date' },
      { component: 'Dropdown', when: 'only a handful of fixed times are offered' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'Visible label. Always pass the real text; `hideLabel` hides it and keeps the name. An empty string leaves the field unnamed — the older opt-out, still honoured.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a field whose meaning is already on screen: a table cell, a filter bar, a row editor. Pass the real `label` with it.' },
      { name: 'value', type: 'TimeString', default: "''", description: '24-hour `HH:mm`, or empty. Anything unparseable is treated as empty rather than guessed at.' },
      { name: 'defaultValue', type: 'TimeString', description: 'Uncontrolled: the time the field starts with; it then keeps its own. Ignored while the controlled prop is passed.' },
      { name: 'onChange', type: '(value: TimeString) => void', description: 'Fires with the 24-hour string once hour, minute and meridiem all exist.' },
      {
        name: 'minuteStep',
        type: 'number',
        default: '1',
        description: 'Granularity of the minute COLUMN. Every minute by default, because the native control this replaced allowed that. It never restricts typing — a coarse column stays a convenience, not a rule to fight.',
      },
      {
        name: 'defaultMinute',
        type: 'string',
        description: "Opt-in, zero-padded (e.g. '00'). Fills an EMPTY minute when an hour is picked, or when a bare typed hour is blurred, so the value completes. A minute already chosen is never overwritten. Unset, a partial selection still reports ''.",
      },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Field height on the control grid.' },
      { name: 'labelSize', type: "'sm' | 'md'", default: "'md'", description: 'Label rung.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'Marks the label and the control.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Paints the error state.' },
      { name: 'helperText', type: 'React.ReactNode', default: "''", description: 'The hint under the field. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks the field and the picker.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the field wrapper.' },
      {
        name: 'data-testid',
        type: 'string',
        description: 'Templates the inventory: `-input`, `-open`, `-popover`, `-hour-05`, `-minute-06`, `-meridiem-PM`, and `-stored` for the caption showing the 24-hour value.',
      },
    ],
    snippet: `<TimePicker
  label="Run at (UTC)"
  value={runAt}
  onChange={setRunAt}
  minuteStep={15}
  data-testid="schedule-run-at"
/>`,
  },

  {
    name: 'FileUpload',
    section: 'file-upload',
    importPath: 'Components/DesignSystem/FileUpload/FileUpload',
    typeFile: 'FileUpload.ts',
    summary:
      'The dropzone, and only the dropzone. It delivers `File[]` and stops there — listing, validating, uploading and showing progress are the caller job, which is why nothing renders a file list here.',
    useWhen: [
      'The user supplies a file by dropping it or browsing for it.',
      'You already have somewhere to put the resulting files.',
    ],
    insteadUse: [
      { component: 'Progress', when: 'you need to show how far an upload has got' },
      { component: 'Button', when: 'a plain browse button is all the screen has room for' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'Visible label. Always pass the real text; `hideLabel` hides it and keeps the name. It names the dropzone — the part a keyboard reaches — together with the prompt; `accept` describes it. An empty string leaves the prompt as the name — the older opt-out, still honoured.' },
      { name: 'hideLabel', type: 'boolean', default: 'false', description: 'Hides the label visually and keeps it as the accessible name — for a field whose meaning is already on screen: a table cell, a filter bar, a row editor. Pass the real `label` with it.' },
      { name: 'onFilesSelected', type: '(files: File[]) => void', required: true, description: 'Every browse and every drop lands here. Single mode delivers at most one file.' },
      {
        name: 'accept',
        type: 'string',
        description: 'Native accept, which filters the BROWSE dialog only. A drop hands over whatever was dragged, so validate on receipt either way — never trust this as a guard.',
      },
      { name: 'multiple', type: 'boolean', default: 'false', description: 'Allows more than one file per selection.' },
      { name: 'prompt', type: 'string', default: "'Drag and drop, or browse'", description: 'The dropzone headline.' },
      { name: 'required', type: 'boolean', default: 'false', description: 'Marks the label.' },
      { name: 'error', type: 'boolean', default: 'false', description: 'Paints the error state — the zone included.' },
      { name: 'helperText', type: 'React.ReactNode', default: "''", description: 'The hint under the zone — the place to state size and format limits. With `error` and no `errorMessage`, it is re-inked as the error — the older shape.' },
      { name: 'errorMessage', type: 'React.ReactNode', description: 'The error, on its own announced line under the hint. Setting it puts the field in error, and the hint in `helperText` stays in view.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks both the drop and the browse.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'id', type: 'string', description: 'Native id.' },
      { name: 'name', type: 'string', description: 'Native name.' },
      { name: 'onReject', type: '(files: File[]) => void', description: 'Opt in to validation: every file is tested against `accept` — MIME types, type/* wildcards and .ext extensions — on the drop and the browse path alike; matches reach onFilesSelected and the rest come here, for you to explain. Without it nothing is filtered.' },
      { name: 'data-testid', type: 'string', description: 'Templates `${id}-dropzone` and `${id}-input`; the prop itself lands on the input.' },
    ],
    snippet: `<FileUpload
  label="Product catalogue"
  accept=".csv,.xlsx"
  helperText="CSV or XLSX, up to 50 MB."
  onFilesSelected={(files) => queueImport(files[0])}
  data-testid="catalogue-import"
/>`,
  },

  /* ── Navigation ───────────────────────────────────────────────────────────── */
  {
    name: 'Tab',
    section: 'tabs',
    importPath: 'Components/DesignSystem/Tab/Tab',
    typeFile: 'Tab.ts',
    summary:
      'The tablist. Selection follows focus — arrowing to a tab shows it, because the panel is already built — and the active tab carries a 2px underline that slides. A row with more tabs than its column holds scrolls inside the column, keeps the selected tab in view, and leaves room for the focus ring; a row that fits is untouched.',
    useWhen: [
      'One record or screen has several views the user moves between without leaving it.',
      'Every view is worth showing by name, and the set does not change as you work.',
    ],
    insteadUse: [
      { component: 'SegmentedControl', when: 'the choice filters one view instead of swapping panels' },
      { component: 'Menu', when: 'there are too many destinations to list across the top' },
    ],
    props: [
      {
        name: 'tabs',
        type: 'TabItem[]',
        required: true,
        description: '`{ id, label, content?, icon?, rightIcon?, badge?, disabled?, required?, align?, htmlId?, data-testid? }`.',
      },
      { name: 'activeTabId', type: 'string | number', description: 'The selected tab id. Controlled.' },
      { name: 'defaultActiveTabId', type: 'string | number', description: 'Uncontrolled: the tab selected first (default: the first tab). Ignored while `activeTabId` is passed.' },
      { name: 'onChange', type: '(tabId: string | number) => void', description: 'Fires with the newly selected id, from a click or an arrow key.' },
      { name: 'showIcons', type: 'boolean', default: 'true', description: 'Turn off to drop every icon without rebuilding the array.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the tablist container.' },
      { name: 'listClassName', type: 'string', default: "''", description: 'Lands on the ROW with the bottom rule, which holds the tablist. For a host that insets the row without shortening the rule (SidePanel passes `tw-pl-3`).' },
      { name: 'panelId', type: 'string', description: 'The id of a tabpanel the HOST renders, as SidePanel does: the selected tab points aria-controls at it. Ignored when the tabs carry their own content.' },
      { name: 'aria-label', type: 'string', native: true, description: 'Native — aria-* attributes reach the tablist, so this names the tab list. Everything else not listed above, and the ref, reach the root.' },
      { name: 'data-testid', type: 'string', description: 'On the root. Templates `-list` (the row that draws the rule, where `listClassName` lands), `-tablist` (the tablist, which scrolls when the tabs outgrow their column and carries `data-overflowing` while it does) and `-indicator`. Each tab carries its own.' },
    ],
    snippet: `<Tab
  activeTabId={activeTab}
  onChange={setActiveTab}
  tabs={[
    { id: 'products', label: 'Products', required: true, badge: 12 },
    { id: 'availability', label: 'Availability', required: true },
    { id: 'schedule', label: 'Schedule', align: 'end' },
  ]}
  data-testid="connector-tabs"
/>`,
  },

  {
    name: 'Menu',
    section: 'menu',
    importPath: 'Components/DesignSystem/Menu/Menu',
    typeFile: 'Menu.ts',
    summary:
      'The popover list of actions, with everything a real menu needs: sections, radio items, destructive tone, shortcuts, descriptions, and slots above and below for the things ARIA will not allow between menu items.',
    useWhen: [
      'A trigger opens a list of commands rather than a list of values.',
      'The list needs grouping, a current selection, or a widget that is not a menu item.',
    ],
    insteadUse: [
      { component: 'DropdownButton', when: 'a flat list of actions is all you need' },
      { component: 'Dropdown', when: 'the user is choosing a value the form keeps' },
    ],
    props: [
      { name: 'items', type: 'MenuItem[]', description: 'Flat list. Provide this OR `sections` — `sections` wins if both are given. An item with `submenu` expands its children inline, under it, as a group it names: ArrowRight or Enter opens and moves into it, ArrowLeft leaves it, Escape on a child closes just the submenu, and ArrowDown walks through the open children like any other row. A chosen radio row (`selected` set) shows the set\'s check, as a chosen Dropdown option does. A `disabled` row is aria-disabled: the arrows and type-ahead reach it and its `tooltip` opens for the keyboard too (so put the reason it is unavailable there), but it never activates. Opening the menu lands on the first enabled row.' },
      { name: 'sections', type: 'MenuSection[]', description: 'Labelled groups, with dividers drawn between them for you.' },
      {
        name: 'header',
        type: 'React.ReactNode',
        description:
          'Rendered inside the popover but OUTSIDE the menu element, above a rule — an identity block, say. A menu may contain only items, groups and separators, which is why this is a slot rather than an item. Brings no padding of its own.',
      },
      { name: 'footer', type: 'React.ReactNode', description: 'The mirror of `header`, below a rule. Where a non-menu widget lives — a segmented control, a hint line.' },
      { name: 'onItemClick', type: '(itemId: string | number) => void', description: 'Fires with the clicked item id, alongside that item own `onClick`.' },
      { name: 'variant', type: "'default' | 'compact'", default: "'default'", description: 'Row density — 36px rows, or 28px for a long list.' },
      { name: 'maxHeight', type: 'string', description: 'Caps the list and makes it scroll. Any CSS length.' },
      {
        name: 'onDismiss',
        type: '() => void',
        description:
          'Opt in to dismissal. Menu is presentational — the caller decides whether it is mounted — so it cannot own an open flag. Supply this and it closes itself on Escape and on an outside pointer-down, calling back so you can unmount it.',
      },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the popover surface.' },
      { name: 'aria-labelledby', type: 'string', native: true, description: 'Native — aria-* attributes reach the role="menu" element, so pointing this at the trigger names the menu. Everything else not listed above, and the ref, reach the surface.' },
      { name: 'trigger', type: 'React.ReactElement', description: 'The element that opens the menu — a Button or an IconButton. With it the Menu anchors itself: it keeps its open state, renders beside the trigger (flipping and staying on screen), and runs the menu-button keyboard — Enter, Space or ArrowDown open on the first item, ArrowUp on the last, Escape and Tab close, a pick closes, and each hands focus back to the trigger. The trigger gets aria-haspopup, aria-expanded and aria-controls, and names the menu. Without it, Menu is the surface you position yourself.' },
      { name: 'open', type: 'boolean', description: 'With `trigger`: controls the open state. Omit it and the menu keeps its own.' },
      { name: 'onOpenChange', type: '(open: boolean) => void', description: 'With `trigger`: reports every open and close.' },
      { name: 'align', type: "'start' | 'end'", default: "'start'", description: 'With `trigger`: the trigger edge the menu lines up with — `end` for a trigger near the far edge.' },
      { name: 'data-testid', type: 'string', description: 'Applied to the container; items carry their own.' },
    ],
    snippet: `<Menu
  onDismiss={() => setOpen(false)}
  onItemClick={runAction}
  sections={[
    { label: 'Edit', items: [
      { id: 'rename', label: 'Rename', shortcut: 'F2' },
      { id: 'duplicate', label: 'Duplicate', description: 'Copies the rule and its conditions' },
    ] },
    { label: 'Danger zone', items: [
      { id: 'delete', label: 'Delete rule', tone: 'danger', shortcut: 'Shift+Delete' },
    ] },
  ]}
  data-testid="rule-actions-menu"
/>`,
  },

  {
    name: 'SelectionBar',
    section: 'table',
    importPath: 'Components/DesignSystem/SelectionBar/SelectionBar',
    typeFile: 'SelectionBar.ts',
    summary:
      'The bar a selectable Table raises: how many rows are ticked, the bulk actions, and the way out. A toolbar on the raised surface that floats at the bottom of the viewport inside the page flow.',
    useWhen: [
      'A Table is `selectable` and the selection has actions that apply to all of it at once.',
      'The count itself is information the user watches while ticking.',
    ],
    insteadUse: [
      { component: 'Menu', when: 'the actions apply to ONE row — a kebab on the row is the shape' },
      { component: 'Toast', when: 'you are reporting an outcome, not offering an action on a selection' },
    ],
    props: [
      { name: 'count', type: 'number', required: true, description: 'How many things are selected — rendered as a `Badge selected sm`, announced as a status when it changes.' },
      { name: 'noun', type: 'string', default: "'selected'", description: 'The word after the count.' },
      { name: 'onClear', type: '() => void', required: true, description: 'Empties the selection. Renders the trailing ×.' },
      { name: 'clearLabel', type: 'string', default: "'Clear selection'", description: 'Accessible name for the ×.' },
      { name: 'label', type: 'string', default: "'Selection actions'", description: 'Accessible name for the toolbar.' },
      { name: 'placement', type: "'sticky' | 'static'", default: "'sticky'", description: '`sticky` floats at the viewport\'s bottom edge while its natural place is below the fold — no layer rung, always under a dialog. `static` renders in place.' },
      { name: 'children', type: 'React.ReactNode', description: 'The bulk actions, as ordinary Buttons (`subtle` for the safe ones, `danger` for the destructive one). Left/Right/Home/End walk them.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the toolbar.' },
      { name: 'data-testid', type: 'string', description: 'Seeds `-count` and `-clear`.' },
    ],
    snippet: `{selectedKeys.length > 0 && (
  <SelectionBar count={selectedKeys.length} onClear={() => setSelectedKeys([])}>
    <Button variant="subtle" size="sm" onClick={exportRows}>Export</Button>
    <Button variant="danger" size="sm" onClick={confirmDelete}>Delete</Button>
  </SelectionBar>
)}`,
  },
  {
    name: 'Kbd',
    section: 'kbd',
    importPath: 'Components/DesignSystem/Kbd/Kbd',
    typeFile: 'Kbd.ts',
    summary:
      'One keycap. A shortcut is a ROW of them — Menu builds that row for you from an item shortcut string; a caller composing its own uses `keysOf()` from the same module.',
    useWhen: [
      'Prose or a hint line names a key the user can press.',
      'You are building a shortcut display outside a Menu.',
    ],
    insteadUse: [
      { component: 'Menu', when: 'the shortcut belongs to a menu item — pass `shortcut` and the caps are drawn for you' },
      { component: 'Tag', when: 'the thing in the box is a value, not a key' },
    ],
    props: [
      { name: 'platform', type: "'mac' | 'other' | 'auto'", default: "'auto'", description: 'Which glyph vocabulary the cap prints: ⌘ ⌥ ⇧ ⌃ on macOS, Ctrl / Alt / Shift elsewhere (Meta prints Ctrl there); Enter, Escape and the arrows become ↵ Esc ↑↓←→ on both. Display only — `aria-keyshortcuts` keeps the ARIA spelling, and `data-key` on the cap carries it.' },
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The key name. One cap per element — split a chord with `keysOf()`.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the kbd element.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `{keysOf('Control+K').map((key) => (
  <Kbd key={key}>{key}</Kbd>
))}`,
  },

  {
    name: 'Breadcrumbs',
    section: 'breadcrumbs',
    importPath: 'Components/DesignSystem/Breadcrumbs/Breadcrumbs',
    typeFile: 'Breadcrumbs.ts',
    summary:
      'The trail back up. Root first, current page last — and the last item renders as the current page whatever you pass it, because a page does not link to itself.',
    useWhen: [
      'The user is somewhere down a hierarchy and needs the way back out.',
      'The path itself tells them where they are.',
    ],
    insteadUse: [
      { component: 'Tab', when: 'the destinations are siblings rather than ancestors' },
      { component: 'Link', when: 'there is only one place to go back to' },
    ],
    props: [
      {
        name: 'items',
        type: 'BreadcrumbItem[]',
        required: true,
        description:
          '`{ label, href?, onClick?, data-testid? }`, root first. An item with `href` is a real anchor — prefer it whenever a URL exists, because middle-click and open-in-new-tab only work on one. With neither href nor onClick it renders as a plain span: a non-navigable waypoint, never a focusable no-op.',
      },
      { name: 'data-testid', type: 'string', description: 'Applied to the nav landmark.' },
    ],
    snippet: `<Breadcrumbs
  data-testid="product-breadcrumbs"
  items={[
    { label: 'Products', href: '/gms/products', onClick: go('/gms/products') },
    { label: 'Fasteners' },
    { label: 'Hex Bolt M8×40' },
  ]}
/>`,
  },

  {
    name: 'TreeView',
    section: 'tree-view',
    importPath: 'Components/DesignSystem/TreeView/TreeView',
    typeFile: 'TreeView.ts',
    summary:
      'A hierarchy the keyboard can walk: arrows move, Right and Left open and close, Enter picks, letters jump. Every node carries its level and position for a screen reader, and a branch can load its children when it opens.',
    useWhen: [
      'The data is a hierarchy the user browses and picks from — categories, a page outline, folders.',
      'Levels open and close, and the user needs to see where a node sits.',
    ],
    insteadUse: [
      { component: 'Accordion', when: 'there is one level of sections holding content, not a hierarchy of items' },
      { component: 'Menu', when: 'the choices are commands, with at most a submenu' },
      { component: 'Table', when: 'each item has columns of data; its rows can expand' },
      { component: 'Combobox', when: 'the user knows the name and should search for it' },
    ],
    props: [
      { name: 'nodes', type: 'TreeNode[]', required: true, description: '`{ id, label, icon?, children?, hasChildren?, disabled? }`. The id must be unique in the whole tree. `hasChildren` marks a parent whose children are not loaded yet; `disabled` means it cannot be selected, and it can still be opened. An empty array renders nothing — a tree must own items, so show a Skeleton or an EmptyState there.' },
      { name: 'expandedKeys', type: 'string[]', description: 'The open parents. Controlled — omit it and pass `defaultExpandedKeys` for a tree that keeps its own.' },
      { name: 'defaultExpandedKeys', type: 'string[]', description: 'Uncontrolled: the parents open at first; the tree then keeps its own.' },
      { name: 'onExpandedChange', type: '(keys: string[]) => void', description: 'Fires with the whole new set of open parents. Load a lazy parent\'s children here, and list it in `loadingKeys` meanwhile.' },
      { name: 'selectedKey', type: 'string | null', description: 'The selected node, or null for none. Controlled — omit it and pass `defaultSelectedKey` for a tree that keeps its own.' },
      { name: 'defaultSelectedKey', type: 'string | null', description: 'Uncontrolled: the node selected at first.' },
      { name: 'onSelectionChange', type: '(key: string) => void', description: 'Fires when a node is picked: click, Enter or Space. Moving focus never selects, so walking the tree does not load every node it passes.' },
      { name: 'loadingKeys', type: 'string[]', default: '[]', description: 'Parents whose children are loading. Each shows a spinner in place of its chevron, announced, and is marked busy.' },
      { name: 'loadingLabel', type: 'string', default: "'Loading…'", description: 'The words the spinner announces.' },
      { name: 'aria-label', type: 'string', native: true, description: 'Name the tree: this, or aria-labelledby pointing at the heading above it.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the tree.' },
      { name: 'data-testid', type: 'string', description: 'Lands on the tree; nodes template `-node-<id>`, a loading node\'s spinner `-node-<id>-loading`.' },
    ],
    snippet: `<TreeView
  aria-label="Categories"
  nodes={categoryTree}
  expandedKeys={open}
  onExpandedChange={(keys) => { setOpen(keys); loadMissingChildren(keys); }}
  loadingKeys={loading}
  selectedKey={categoryId}
  onSelectionChange={setCategoryId}
  data-testid="category-tree"
/>`,
  },

  {
    name: 'Link',
    section: 'link',
    importPath: 'Components/DesignSystem/Link/Link',
    typeFile: 'Link.ts',
    summary:
      'The one anchor spelling: accent ink, always underlined. The underline is not decoration — a link told apart from body text by colour alone fails contrast requirements for anyone who cannot see that difference.',
    useWhen: [
      'Text navigates somewhere — in prose, in a table cell, in a description.',
      'The destination is external and the user should be told before they leave.',
    ],
    insteadUse: [
      { component: 'Button', when: 'it performs an action rather than going somewhere' },
      { component: 'Breadcrumbs', when: 'it is the path back up a hierarchy' },
    ],
    props: [
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The link text. Make it name the destination — never "click here".' },
      {
        name: 'external',
        type: 'boolean',
        default: 'false',
        description: 'Opens in a new tab with the safe rel attributes, adds the external glyph, and announces "(opens in new tab)" so the departure is told rather than sprung.',
      },
      {
        name: 'as',
        type: 'React.ElementType',
        default: "'a'",
        description: 'The element to render. Pass a router Link to navigate client-side — the design system cannot import the router itself, so this prop is the seam.',
      },
      { name: 'to', type: 'string', description: 'Forwarded to the `as` element — a router Link destination. Meaningless on the default anchor.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the anchor.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Link as={RouterLink} to={\`/gms/products/\${product.id}\`}>
  {product.name}
</Link>

<Link href="https://docs.compatio.ai/imports" external>
  Import format reference
</Link>`,
  },

  {
    name: 'Pagination',
    section: 'pagination',
    importPath: 'Components/DesignSystem/Pagination/Pagination',
    typeFile: 'Pagination.ts',
    summary:
      'The page bar, with a constant-width run of page slots so the control never reflows as you move through it, plus the "Showing 26–50 of 312" line.',
    useWhen: [
      'A list is served a page at a time and the user needs to reach a specific one.',
      'Knowing how much there is in total is part of reading the list.',
    ],
    insteadUse: [
      { component: 'Dropdown', when: 'an infinite list should load its next slice on scroll instead' },
      { component: 'Table', when: 'the whole set fits and sorting is the real need' },
    ],
    props: [
      { name: 'page', type: 'number', required: true, description: 'Current page, 1-based. Controlled — the component never holds page state.' },
      { name: 'totalItems', type: 'number', required: true, description: 'Total across all pages, which is what the summary counts.' },
      { name: 'pageSize', type: 'number', required: true, description: 'Items per page. With `totalItems` it decides how many pages exist.' },
      { name: 'onPageChange', type: '(page: number) => void', required: true, description: 'Fires with the requested page number.' },
      { name: 'siblingCount', type: 'number', default: '1', description: 'Pages shown either side of the current one. The default gives a constant 7 slots, so the bar never changes width.' },
      { name: 'pageSizeOptions', type: 'number[]', description: 'With onPageSizeChange, renders a compact rows-per-page Dropdown before the summary. Unset, nothing renders.' },
      { name: 'onPageSizeChange', type: '(pageSize: number) => void', description: 'The new size. Reset page to 1 in the handler — row 26 of 25-per-page is row 1 of 50-per-page.' },
      { name: 'pageSizeLabel', type: 'string', default: "'Rows per page'", description: 'The page-size control\'s accessible name.' },
      { name: 'showSummary', type: 'boolean', default: 'true', description: 'The "Showing 26–50 of 312" line.' },
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Control-grid rung. Use `sm` under a dense table.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks the whole bar — while a page is loading, say. The current page keeps its marker, and the button you pressed keeps focus: a button disabled while it holds focus is aria-disabled until focus leaves (natively disabled, the browser would throw focus to the page). The same holds for Next reaching the last page.' },
      { name: 'label', type: 'string', default: "'Pagination'", description: 'Accessible name of the landmark. Narrow it when a page has two.' },
      { name: 'previousLabel', type: 'string', default: "'Previous page'", description: 'Overridable for translation.' },
      { name: 'nextLabel', type: 'string', default: "'Next page'", description: 'Overridable for translation.' },
      { name: 'pageLabel', type: '(page: number) => string', default: '(n) => `Go to page ${n}`', description: 'Accessible name for a numbered button.' },
      { name: 'id', type: 'string', description: 'Native id on the landmark.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Pagination
  page={page}
  pageSize={25}
  totalItems={total}
  onPageChange={setPage}
  size="sm"
  disabled={isFetching}
  data-testid="products-pagination"
/>`,
  },

  /* ── Data display ─────────────────────────────────────────────────────────── */
  {
    name: 'Table',
    section: 'table',
    importPath: 'Components/DesignSystem/Table/Table',
    typeFile: 'Table.ts',
    summary:
      'The data grid: sortable headers that are real buttons, pinned columns and header, expandable rows, and its own loading and empty states. It holds no state of its own — sort, expansion and paging all belong to the caller.',
    useWhen: [
      'Records are read in rows and compared down columns.',
      'The user needs to sort, pin, or open a row to see more.',
    ],
    insteadUse: [
      { component: 'Card', when: 'each record is read on its own rather than compared' },
      { component: 'Accordion', when: 'the list is short and every row is really a disclosure' },
    ],
    props: [
      {
        name: 'columns',
        type: 'TableColumn<T>[]',
        required: true,
        description: '`{ id, header, accessor, width?, align?, sortable?, cellType?, render?, sticky? }`. `accessor` takes a key or a function; `render` receives `(value, row, rowIndex)`.',
      },
      { name: 'data', type: 'T[]', required: true, description: 'The rows.' },
      { name: 'rowKey', type: 'keyof T | ((row: T) => string | number)', required: true, description: 'Stable identity per row. Everything keyed — expansion especially — hangs off this, so never derive it from the index.' },
      {
        name: 'headerVariant',
        type: "'dark' | 'light'",
        description:
          'Defaults with the zebra: a plain table takes `light`, because a header LABELS the data and sits behind it; a striped table takes `dark`, because the stripe IS the sunken ground and a light head on it reads as one more row.',
      },
      { name: 'density', type: "'compact' | 'default' | 'comfortable'", default: "'default'", description: '32 / 36 / 44px rows. The type size never changes with it.' },
      { name: 'striped', type: 'boolean', default: 'false', description: 'Alternating row grounds.' },
      {
        name: 'isRowSunken',
        type: '(row: T, rowIndex: number) => boolean',
        description: 'Caller-owned row grounds, replacing index striping entirely. For a zebra that encodes GROUPING rather than position — a parent and its expanded versions sharing one ground.',
      },
      { name: 'hoverable', type: 'boolean', default: 'false', description: 'Row hover ground. Turn it on when rows are clickable.' },
      { name: 'bordered', type: 'boolean', default: 'true', description: 'The outer border.' },
      {
        name: 'stickyHeader',
        type: 'boolean',
        default: 'false',
        description: 'Pins the header while the body scrolls. Pair it with `maxHeight` — sticky needs a scrolling ancestor, and without a bounded box the pin appears to do nothing at all. Keyboard focus is never hidden under the pin: the table pads its scroll edge by the header\'s height.',
      },
      { name: 'maxHeight', type: 'string', description: 'Bounds the height and lets the body scroll. This is what makes the wrapper the ancestor `stickyHeader` resolves against.' },
      { name: 'sortState', type: 'TableSortState', description: 'Current sort. Controlled. A sortable column rests its double chevron at 90% and brightens it on hover or focus, so which columns offer a sort is visible without a pointer — the sorted column shows a single arrow instead.' },
      { name: 'onSort', type: '(columnId: string, direction: SortDirection) => void', description: 'Fires with the requested column and direction.' },
      { name: 'onRowClick', type: '(row: T, rowIndex: number) => void', description: 'Row click. Pair it with `hoverable` so the row looks as clickable as it is. The rows become ONE Tab stop: Tab enters on one row, ArrowUp / ArrowDown / Home / End move between rows, Enter or Space opens the focused one, and controls inside a row keep their own Tab stops.' },
      { name: 'rowActionHint', type: 'string', default: "'Press Enter to open'", description: 'What a clickable row does, read to a screen reader after its cells — a row is a focusable table row with no widget role, so without it nothing says Enter acts. Only with `onRowClick`; say what the row really does when it is not "open".' },
      { name: 'emptyContent', type: 'React.ReactNode', description: 'What renders instead of rows when `data` is empty — an EmptyState, usually.' },
      { name: 'loading', type: 'boolean', default: 'false', description: 'Swaps the body for the loading treatment.' },
      { name: 'footer', type: 'React.ReactNode', description: 'A band under the last row — totals, or a pagination bar.' },
      {
        name: 'renderExpandedRow',
        type: '(row: T, rowIndex: number) => React.ReactNode',
        description: 'Supplying it turns on the expand column. The node lands in a cell spanning the whole grid, so it can hold anything — a nested Table included. It must not fetch; expansion is the caller state.',
      },
      { name: 'expandedRowKeys', type: 'Array<string | number>', description: 'Which rows are open, by `rowKey`. Controlled — the table holds no expansion state, because a component owning half the state is the one that drifts.' },
      { name: 'onExpandChange', type: '(rowKey: string | number, expanded: boolean) => void', description: 'Fires with the state being moved TO.' },
      { name: 'isRowExpandable', type: '(row: T) => boolean', description: 'Per-row veto. A vetoed row gets an empty cell rather than a chevron that does nothing.' },
      { name: 'expandLabel', type: '(row: T, expanded: boolean) => string', description: 'Accessible name for the chevron. Defaults to Expand row / Collapse row.' },
      {
        name: 'isRowExpandLoading',
        type: '(row: T) => boolean',
        description: 'This row is waiting on its children: the chevron becomes a spinner and disables. The chevron is what the user clicked, so the chevron is where the wait belongs — and disabling it is what stops a second request. Pressed from the keyboard it keeps focus while it waits (aria-disabled until focus leaves).',
      },
      { name: 'selectable', type: 'boolean', default: 'false', description: 'Adds a leading checkbox column. The header box selects the page (indeterminate on a partial set). Selected rows take the selection tint, drop their hover and carry `data-selected="true"`. Clicking a box never fires `onRowClick`.' },
      { name: 'selectedKeys', type: 'Array<string | number>', description: 'The selected row keys. Controlled — the table holds no selection of its own, exactly like sort and expansion.' },
      { name: 'onSelectionChange', type: '(keys: Array<string | number>) => void', description: 'Fires with the NEXT complete key array. Keys not on this page are kept, so a paged list can accumulate a selection.' },
      { name: 'isRowSelectable', type: '(row: T) => boolean', description: 'Rows that may not be selected render an empty cell rather than a dead box; the header box skips them.' },
      { name: 'selectionLabel', type: '(row: T) => string', description: 'Accessible name for a row\'s box. Name the record: `(row) => \`Select ${row.name}\``.' },
      { name: 'selectAllLabel', type: 'string', default: "'Select all rows'", description: 'Accessible name for the header box.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the table.' },
      { name: 'id', type: 'string', description: 'DOM id for the scrolling region.' },
      { name: 'caption', type: 'string', description: 'The table\'s accessible name, as a visually hidden caption, read as the table is entered ("Products, table, 6 columns"). Name the records, not the widget.' },
      { name: 'role', type: 'string', native: true, description: 'Native — everything not listed above, and the ref, reach the scrolling region: for a keyboard-scrollable table, role="region" with an aria-label and tabIndex={0}. Name the TABLE with caption.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook; rows and cells template off it — `-select-all`, `-row-<key>-select`.' },
    ],
    snippet: `<Table<Product>
  columns={columns}
  data={products}
  rowKey="id"
  density="compact"
  stickyHeader
  maxHeight="60vh"
  sortState={sort}
  onSort={(columnId, direction) => setSort({ columnId, direction })}
  emptyContent={<EmptyState title="No products match these filters" />}
  data-testid="products-table"
/>`,
  },

  {
    name: 'Card',
    section: 'card',
    importPath: 'Components/DesignSystem/Card/Card',
    typeFile: 'Card.ts',
    summary:
      'A bounded surface for one thing: a title that is a real heading, a description, your content, and an optional action band along the bottom.',
    useWhen: [
      'Each record is read on its own rather than compared against its neighbours.',
      'A panel needs its own ground and its own actions.',
    ],
    insteadUse: [
      { component: 'Table', when: 'the records are compared down columns' },
      { component: 'Accordion', when: 'the content should start collapsed' },
    ],
    props: [
      { name: 'title', type: 'string', description: 'The card heading.' },
      { name: 'description', type: 'string', description: 'A line under the title.' },
      { name: 'footer', type: 'React.ReactNode', description: 'Right-aligned action band with a top rule, mirroring a dialog footer.' },
      {
        name: 'elevation',
        type: "'none' | 'sm' | 'md'",
        default: "'sm'",
        description: '`sm` is the resting card; `md` is one that genuinely floats — dragged, or pinned. Higher belongs to the overlay tier, and a card claiming it competes with the modal layer.',
      },
      {
        name: 'interactive',
        type: 'boolean',
        default: 'false',
        description: 'Hover lift for a card the user can act on: the shadow steps sm → md, the card rises 1px and its edge firms to `edge-strong`, so the lift reads in dark too. Visual only — for a card that IS the target, pass `action`, which brings the semantics and this lift with it.',
      },
      {
        name: 'action',
        type: 'CardAction',
        description: 'Makes the whole card one target, named by its title: `{ href }` renders the title as a real link, `{ onClick }` alone as a button, and its hit area is stretched over the card. The keyboard meets one control, described by `description`, and the focus ring outlines the card. The footer stays above it, so its buttons remain their own. Requires `title`. A router caller passes both halves — `href` for the browser, `onClick` to navigate in the app.',
      },
      {
        name: 'titleAs',
        type: "'h2' | 'h3' | 'h4'",
        default: "'h3'",
        description: 'Heading level for the title. It is a prop because only the page knows its own outline — and a page of cards whose titles are spans is a page with no structure to navigate.',
      },
      { name: 'children', type: 'React.ReactNode', description: 'The card body.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the card.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Card
  title="Import summary"
  titleAs="h2"
  description="Last run 12 minutes ago"
  footer={<Button variant="secondary" size="sm">View log</Button>}
  data-testid="import-summary-card"
>
  <ImportStats run={lastRun} />
</Card>`,
  },

  {
    name: 'Accordion',
    section: 'accordion',
    importPath: 'Components/DesignSystem/Accordion/Accordion',
    typeFile: 'Accordion.ts',
    summary:
      'The disclosure: a real button over a real region, with the chevron and the focus treatment. Closed content UNMOUNTS — whatever state lives inside resets on collapse.',
    useWhen: [
      'A section is worth showing by name but not by default.',
      'You want to fetch a section contents the first time it opens.',
    ],
    insteadUse: [
      { component: 'Tab', when: 'the sections are alternatives rather than additions' },
      { component: 'Card', when: 'the content should always be visible' },
    ],
    props: [
      { name: 'title', type: 'React.ReactNode', required: true, description: 'Header content, rendered inside the disclosure button.' },
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The body. It unmounts when closed — lift any state that must survive a collapse.' },
      { name: 'defaultOpen', type: 'boolean', default: 'false', description: 'Uncontrolled initial state; the component owns the toggle after mount.' },
      {
        name: 'open',
        type: 'boolean',
        description: 'Controlled state. Provide it and the accordion follows you — `onOpenChange` then reports the state the click ASKED for, and nothing moves until you re-render. This is what makes an exclusive single-open list possible.',
      },
      { name: 'onOpenChange', type: '(open: boolean) => void', description: 'Fires with the new state, or the requested one when controlled. Lazy work on first expand keys off it being true.' },
      {
        name: 'actions',
        type: 'React.ReactNode',
        description: 'Header-row actions beside the chevron, rendered as a SIBLING of the button — never inside it, because nested interactive controls are invalid and unreachable. Clicking one does not toggle the section.',
      },
      {
        name: 'variant',
        type: "'divided' | 'boxed'",
        default: "'divided'",
        description: '`divided` is the hairline over each section, for a stacked list. `boxed` is a standalone section: bordered, rounded, on the surface ground with its own inset. Use it instead of drawing the box with `className` — that overrode the component\'s own border.',
      },
      {
        name: 'headingLevel',
        type: '2 | 3 | 4 | 5 | 6',
        description: 'The outline level of the section header. The button renders inside a heading of that level that holds nothing else, so a screen-reader user can move between sections by heading; the row looks exactly the same. Omitted, there is no heading — the right level depends on the page around the accordion, so it is yours to name. The heading carries `-heading`.',
      },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper, for layout (margin, width). Framing belongs to `variant`.' },
      { name: 'data-testid', type: 'string', description: 'Lands on the BUTTON — the element a spec clicks. The body derives `-region`.' },
    ],
    snippet: `<Accordion
  title="Availability rules"
  headingLevel={3}
  open={openSection === 'availability'}
  onOpenChange={(next) => setOpenSection(next ? 'availability' : null)}
  actions={<IconButton aria-label="Edit availability"><Edit /></IconButton>}
  data-testid="availability-section"
>
  <AvailabilityRules rules={rules} />
</Accordion>`,
  },

  {
    name: 'Divider',
    section: 'divider',
    importPath: 'Components/DesignSystem/Divider/Divider',
    typeFile: 'Divider.ts',
    summary:
      'A rule between things. The quietest way to group — reach for space first, and for this only when space alone is not reading as a break.',
    useWhen: [
      'Two runs of content need separating and a heading would be too loud.',
      'A toolbar needs its groups told apart.',
    ],
    insteadUse: [
      { component: 'Card', when: 'the sections deserve their own grounds' },
      { component: 'Typography', when: 'the break wants a name rather than a line' },
    ],
    props: [
      { name: 'orientation', type: "'horizontal' | 'vertical'", default: "'horizontal'", description: 'Vertical needs a parent with a height to fill.' },
      { name: 'variant', type: "'solid' | 'dashed'", default: "'solid'", description: 'Dashed reads as provisional — a placeholder boundary rather than a settled one.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the rule. Spacing goes here.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Divider className="tw-my-6" />
<Divider orientation="vertical" className="tw-h-6" />`,
  },

  {
    name: 'Tag',
    section: 'tag',
    importPath: 'Components/DesignSystem/Tag/Tag',
    typeFile: 'Tag.ts',
    summary:
      'The chip that DISPLAYS a value — one the user picked, one that describes a record, or one carrying a state. Optionally removable.',
    useWhen: [
      'Showing an applied filter, a multi-select value, or an attribute in a cell.',
      'A value carries a state — added, updated, removed — that colour should help tell.',
    ],
    insteadUse: [
      { component: 'ChoiceChip', when: 'the chip IS the control that makes the choice' },
      { component: 'Status', when: 'it reports the state of a record rather than naming a value' },
      { component: 'Badge', when: 'it is a count' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'The value. Truncates past roughly 160px, with the full text on `title`.' },
      { name: 'onRemove', type: '(e) => void', description: 'Renders the remove affordance and receives its click. Without it the chip is read-only with no button at all — not a disabled one.' },
      { name: 'removeLabel', type: 'string', description: 'Accessible name for the remove button. Defaults to "Remove {label}" — override when the label alone does not describe the value, like "42".' },
      {
        name: 'variant',
        type: "'selected' | 'meta'",
        default: "'selected'",
        description: '`selected` is the selection grammar, for values the user PICKED. `meta` is the quiet pair, for values that merely DESCRIBE. A metadata chip in selection colours claims a choice nobody made.',
      },
      { name: 'outlined', type: 'boolean', default: 'false', description: 'Draws the chip as a bordered rectangle in its own ink, for chips whose tint alone does not separate them from the ground.' },
      {
        name: 'tone',
        type: "'neutral' | 'info' | 'success' | 'warning' | 'danger'",
        description: 'The chip STATE, when it has one. Supplying it OVERRIDES `variant`. Reach for it only when the colour carries information — a filter chip has no state to report.',
      },
      {
        name: 'showToneGlyph',
        type: 'boolean',
        default: 'false',
        description: 'Adds the tone glyph before the label. REQUIRED whenever tone is the only thing separating two chips — in a diff row, added and updated are otherwise identical shapes in different hues.',
      },
      {
        name: 'stateLabel',
        type: 'string',
        description: 'What the colour stands for, announced and not drawn — the glyph is hidden from assistive tech, so it only ever helped sighted readers. Reads as a continuation of the label: "description, new".',
      },
      { name: 'removeIcon', type: 'React.ReactNode', description: 'Replaces the remove glyph — for an action that UNDOES rather than removes. Pair it with an accurate `removeLabel`, or the default becomes a lie.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Recedes the chip and blocks removal.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook; the remove button derives `-remove`.' },
    ],
    snippet: `<Tag
  label={filter.label}
  onRemove={() => clearFilter(filter.id)}
  data-testid={\`filter-chip-\${filter.id}\`}
/>

<Tag label="description" tone="success" showToneGlyph stateLabel="new" />`,
  },

  {
    name: 'ChoiceChip',
    section: 'tag',
    importPath: 'Components/DesignSystem/ChoiceChip/ChoiceChip',
    typeFile: 'ChoiceChip.ts',
    summary:
      'The chip that IS the control. A real toggle button with a pressed state — where Tag displays a choice already made, this one makes it.',
    useWhen: [
      'A filter panel offers a cloud of options to switch on and off.',
      'The options are short and seeing them all together matters.',
    ],
    insteadUse: [
      { component: 'Tag', when: 'the chip shows a choice already made and offers removal' },
      { component: 'Checkbox', when: 'the options read as a list rather than a cloud' },
    ],
    props: [
      { name: 'label', type: 'string', required: true, description: 'The option name.' },
      { name: 'selected', type: 'boolean', required: true, description: 'The toggle state. A selected chip takes the selection tint AND an inner outline — a shape, not only a colour, and the system Highlight in forced colors. It is drawn as an outline so the chip never grows: the pills this replaced grew a border when picked and shifted their neighbours by a pixel.' },
      {
        name: 'tone',
        type: "'neutral' | 'danger'",
        default: "'neutral'",
        description: 'Marks a choice whose unmaking is a destructive PENDING change — an attribute just unselected, where leaving it means the save removes an association. Selected and disabled keep their one grammar regardless. `default` is the older spelling of `neutral`, still honoured.',
      },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Blocks the toggle.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the button.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook. The tone is exposed as `data-tone`, which is what a spec should assert.' },
    ],
    snippet: `{attributes.map((attr) => (
  <ChoiceChip
    key={attr.id}
    label={attr.name}
    selected={selected.has(attr.id)}
    onClick={() => toggle(attr.id)}
    data-testid={\`attribute-chip-\${attr.id}\`}
  />
))}`,
  },

  {
    name: 'Avatar',
    section: 'avatar',
    importPath: 'Components/DesignSystem/Avatar/Avatar',
    typeFile: 'Avatar.ts',
    summary:
      'The identity mark — a picture, or initials derived from a name. It is decorative by default, because it almost always repeats a name that is already on screen.',
    useWhen: [
      'A person needs a mark beside their name, or inside a control that stands for them.',
    ],
    insteadUse: [
      { component: 'ImagePlaceholder', when: 'the missing picture is a product, not a person' },
      { component: 'Badge', when: 'you want a count rather than an identity' },
    ],
    props: [
      {
        name: 'name',
        type: 'string',
        required: true,
        description: 'The person name. Initials come from the first letter of the first two words. An empty name falls back to a single question mark rather than an empty circle.',
      },
      {
        name: 'src',
        type: 'string',
        description: 'A picture, cropped to the circle. The tone ground shows through while it loads, and a failure falls back to the initials — an avatar never renders a broken-image glyph.',
      },
      { name: 'alt', type: 'string', default: "''", description: 'Alt for the image. Empty by default because the mark is decorative by default.' },
      {
        name: 'size',
        type: "'sm' | 'md' | 'lg'",
        default: "'md'",
        description: 'Deliberately NOT the control ladder: an avatar usually sits inside a control, so it runs one rung down. 32px centred in a 36px button is the standard header arrangement.',
      },
      { name: 'tone', type: "'accent' | 'neutral' | 'info' | 'success' | 'purple' | 'auto'", default: "'accent'", description: 'Ground and ink pairing — Status\'s measured tint/ink pairs. `auto` hashes the name onto info / success / purple / neutral (never warning or danger) for a LIST of people; the default stays `accent` because this product\'s header shows one avatar, where a hashed colour is just the loudest thing on screen. The resolved tone is emitted as `data-tone`.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the mark.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Avatar
  name={user.fullName}
  src={user.photoUrl}
  size="md"
  data-testid="profile-avatar"
/>`,
  },

  /* ── Status & feedback ────────────────────────────────────────────────────── */
  {
    name: 'Status',
    section: 'status',
    importPath: 'Components/DesignSystem/Status/Status',
    typeFile: 'Status.ts',
    summary:
      'The state chip. Its tone says what the state MEANS, not what produced it — so a red chip means the same thing on every screen.',
    useWhen: [
      'A record is in a state the reader needs at a glance: generating, promoted, failed.',
      'A state changes in place and should be announced when it does.',
    ],
    insteadUse: [
      { component: 'Tag', when: 'the chip names a VALUE rather than reporting a state' },
      { component: 'Badge', when: 'it is a count' },
      { component: 'Alert', when: 'the state needs a sentence and something to do about it' },
    ],
    props: [
      { name: 'tone', type: "'neutral' | 'info' | 'accent' | 'success' | 'warning' | 'danger'", default: "'neutral'", description: 'What the state means. `info` and `accent` are both "in progress", kept apart so a chip can say WHICH pipeline is running.' },
      { name: 'label', type: 'React.ReactNode', description: 'The chip content. Keep it to a phrase — the chip does not wrap, so a sentence pushes the row height instead.' },
      { name: 'form', type: "'chip' | 'dot'", default: "'chip'", description: 'How the state is drawn. `dot` is a 6px disc in the tone\'s glyph colour beside the label in body ink — for the dense column where a pill per row is a wall of tint. `pulse` breathes the dot; `showAlert` still turns it danger. The element carries `data-form`.' },
      { name: 'timestamp', type: 'string', description: 'A time rendered beneath the chip.' },
      { name: 'showAlert', type: 'boolean', default: 'false', description: 'Forces the danger tone and appends a warning triangle. The triangle is decorative — `alertLabel` is what carries the meaning.' },
      { name: 'alertLabel', type: 'string', default: "'Attention required'", description: 'The hidden text the triangle stands for. Without it the alert is a red fill and a wordless glyph.' },
      { name: 'alertTooltip', type: 'string', description: 'Hover text saying WHY the alert is raised — the failure detail. It adds to `alertLabel`, never replaces it: a tooltip is hover- and pointer-only. Omitted, the triangle renders as before.' },
      {
        name: 'pulse',
        type: 'boolean',
        default: 'false',
        description: 'Marks the state as IN FLIGHT — a dot with a soft halo. Opt-in, never inferred: only the caller knows whether the state is still moving. Hidden from assistive tech, because the label already says "Generating".',
      },
      {
        name: 'live',
        type: 'boolean',
        default: 'false',
        description:
          'Announces a state that changes IN PLACE. Off by default on purpose: a table rendering 50 chips after a fetch would insert 50 live regions, and some screen readers announce every one. Turn it on for a single chip that mutates, never in a list.',
      },
      { name: 'variant', type: 'StatusVariant', deprecated: 'Pass `tone` and `label`. The alias only supplies a default label, which is the caller decision.', description: 'Domain alias kept so the migration could be incremental. Ignored when `tone` is given.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Seeds `-chip`, `-label`, `-alert`, `-timestamp`. The chip carries `data-tone`, which is what a spec should assert instead of classes.' },
    ],
    snippet: `<Status
  tone="info"
  label="Generating"
  pulse
  live
  timestamp="Started 2 min ago"
  data-testid="model-status"
/>`,
  },

  {
    name: 'Badge',
    section: 'badge',
    importPath: 'Components/DesignSystem/Badge/Badge',
    typeFile: 'Badge.ts',
    summary:
      'The count. Its variants name the count CONTEXT rather than a tone — which is why a textual state does not belong here.',
    useWhen: [
      'A number qualifies the thing beside it: unread, matched, pending.',
      'A button or a menu section needs its total shown on it.',
    ],
    insteadUse: [
      { component: 'Status', when: 'the chip carries a word rather than a number' },
      { component: 'Tag', when: 'it names a value the user picked' },
    ],
    props: [
      { name: 'max', type: 'number', description: 'Caps a NUMERIC count — `max={99}` renders 1240 as "99+", with the full, locale-formatted figure in `title` and read in full to assistive technology. Numbers are formatted with the locale\'s thousands separator either way.' },
      {
        name: 'variant',
        type: "'warning' | 'primary' | 'secondary' | 'disabled' | 'neutral' | 'selected'",
        default: "'secondary'",
        description: '`primary` is the bubble ON a filled button, `secondary` the standalone brand pill, `warning` an attention count, `neutral` the QUIET count — a total beside a menu section, where a brand pill would claim urgency the number does not have — and `selected` the count on a chosen thing (the active tab), in the same accent-on-tint pair a selected Tag wears.',
      },
      {
        name: 'size',
        type: "'sm' | 'md'",
        default: "'md'",
        description: '`md` is the 28px chip height that sits level with a Tag. `sm` is the 20px COUNT beside a label — what Tab renders — where a 28px pill reads as a chip rather than a number.',
      },
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The count.' },
      { name: 'icon', type: 'React.ReactNode', description: 'A glyph before the number.' },
      { name: 'showIcon', type: 'boolean', description: 'Defaults to true once an icon is given.' },
      { name: 'className', type: 'string', description: 'Lands on the badge.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Badge variant="neutral" data-testid="unmatched-count">{unmatched.length}</Badge>`,
  },

  {
    name: 'Alert',
    section: 'alert',
    importPath: 'Components/DesignSystem/Alert/Alert',
    typeFile: 'Alert.ts',
    summary:
      'The banner that stays: a condition the user should know about, optionally with something to do about it. It sits in the page rather than over it.',
    useWhen: [
      'A condition persists and the user needs it in front of them while they work.',
      'The message needs a follow-up action or two.',
    ],
    insteadUse: [
      { component: 'Toast', when: 'the message is about something that just happened and can go away' },
      { component: 'Status', when: 'it is one record state rather than a page-level condition' },
      { component: 'ErrorState', when: 'the thing failed entirely and there is nothing to show' },
    ],
    props: [
      { name: 'tone', type: "'neutral' | 'info' | 'success' | 'warning' | 'danger'", default: "'neutral'", description: 'What the condition means.' },
      { name: 'title', type: 'string', description: 'Bold first line. Optional — a one-line alert can be body only.' },
      { name: 'children', type: 'React.ReactNode', description: 'The body.' },
      { name: 'onDismiss', type: '() => void', description: 'Supply it to render a dismiss button. Without it the alert cannot be closed — which is right for a condition that is still true.' },
      { name: 'dismissLabel', type: 'string', default: "'Dismiss'", description: 'Accessible name for that button.' },
      { name: 'actions', type: 'AlertAction[]', description: '`{ label, onClick?, href? }`, rendered as links. Acting does NOT dismiss the banner — the condition is usually still true while the action runs.' },
      { name: 'icon', type: 'React.ReactNode', description: 'The leading mark. Omitted, the tone\'s glyph renders; `null` renders none — for a banner whose content leads with its own mark, such as a progress spinner; any other node replaces the glyph. The screen-reader tone word is unaffected.' },
      {
        name: 'live',
        type: 'boolean',
        default: 'false',
        description: 'Announce on appearance. Off by default because most alerts render WITH the page, and one that announces on mount talks over the page load. Set it when the banner appears in response to something.',
      },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the banner.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Alert
  tone="warning"
  title="3 products have no category"
  live
  actions={[{ label: 'Review them', onClick: goToUnmatched }]}
  data-testid="catalogue-uncategorised-alert"
>
  They will be skipped by the next export.
</Alert>`,
  },

  {
    name: 'ErrorSummary',
    section: 'error-summary',
    importPath: 'Components/DesignSystem/ErrorSummary/ErrorSummary',
    typeFile: 'ErrorSummary.ts',
    summary:
      'What stops a form from submitting, listed at the top of the form: one announcement and one place to start, instead of an alert for every invalid field. Each line moves the user to its field.',
    useWhen: [
      'A submit can fail on more than one field at once.',
      'The form is long enough that an invalid field can be out of view.',
    ],
    insteadUse: [
      { component: 'TextField', when: 'a single field is checked as the user leaves it — its own `errorMessage` line is enough' },
      { component: 'Alert', when: 'the problem is not about a field: a server error, a missing permission' },
    ],
    props: [
      {
        name: 'errors',
        type: 'ErrorSummaryItem[]',
        required: true,
        description: '`{ id, message }`: the id is the field\'s own `id` prop, the message the words its error line uses. Renders nothing while empty. Wrap the form in `ErrorSummaryScope` so the fields stop announcing their own errors.',
      },
      { name: 'title', type: 'string', default: "'There is a problem'", description: 'The heading line.' },
      {
        name: 'submitCount',
        type: 'number',
        description: 'The form\'s submit count. The summary takes focus when errors first appear; pass this so a submit that fails again moves the user back to it. It never takes focus while fields are being corrected.',
      },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the banner.' },
      { name: 'data-testid', type: 'string', description: 'The banner; each line\'s link derives `-link-<id>`.' },
    ],
    snippet: `<ErrorSummaryScope>
  <form noValidate onSubmit={submit}>
    <ErrorSummary errors={errors} submitCount={submitCount} data-testid="invite-errors" />
    <TextField id="invite-email" label="Work email" value={email} onChange={setEmail}
      errorMessage={messageFor('invite-email')} />
    <Button type="submit">Send invite</Button>
  </form>
</ErrorSummaryScope>`,
  },

  {
    name: 'Toast',
    section: 'toast',
    importPath: 'Components/DesignSystem/Toast/useToast',
    typeFile: 'Toast.ts',
    usage: 'api',
    summary:
      'The transient message. You do not render it — you raise it: `toast.success(...)` from anywhere, and the region mounted once at the app root does the rest.',
    useWhen: [
      'Something just finished and the user should know without being interrupted.',
      'A long action needs its "Saving…" replaced in place by "Saved".',
    ],
    insteadUse: [
      { component: 'Alert', when: 'the condition persists and should stay on the page' },
      { component: 'Dialog', when: 'the user must respond before continuing' },
    ],
    props: [
      { name: 'loading', type: 'string', description: '`toast.promise(work, { loading, success, error })` — the message shown, sticky, while the promise is pending. One toast, replaced in place as it settles; the original promise is returned, never swallowed.' },
      { name: 'success', type: 'string | ((value: T) => string)', description: 'The message on resolve; a function receives the value ("Promoted 12 products").' },
      { name: 'error', type: 'string | ((error: unknown) => string)', description: 'The message on reject — sticky, in the danger tone; a function receives the rejection.' },
      { name: 'title', type: 'string', required: true, description: 'The message. `toast.show(title, options)` takes it first.' },
      { name: 'description', type: 'string', description: 'A second line.' },
      { name: 'tone', type: "'neutral' | 'info' | 'success' | 'warning' | 'danger'", default: "'neutral'", description: 'Set for you by the helper you call — `toast.success` raises the success tone.' },
      {
        name: 'duration',
        type: 'number | null',
        description:
          'Milliseconds until auto-dismiss; `null` keeps it until dismissed by hand. Omit for the tone default — 5s, warning 8s, and danger STICKY, because an error that disappears on its own is one the user may never have read. A toast with an `action` is sticky too unless you pass a duration: its follow-up must still be there when a keyboard user reaches it. The stack shows three: a fourth push drops the oldest toast that would have left anyway, never a sticky one, so unread errors can stack past three until dismissed.',
      },
      { name: 'action', type: 'ToastAction', description: '`{ label, onClick }` — one follow-up. It keeps the toast until it is acted on or dismissed, unless you pass `duration`.' },
      { name: 'id', type: 'string', description: 'Supply it to REPLACE an existing toast rather than stack a second one: the "Saving…" to "Saved" case. Omit and one is generated.' },
      { name: 'dismissLabel', type: 'string', description: 'Accessible name for the dismiss button.' },
    ],
    snippet: `import { toast } from 'Components/DesignSystem/Toast/useToast';

const id = toast.info('Saving changes…', { duration: null });
await save();
toast.success('Changes saved', { id });   // replaces, does not stack`,
  },

  {
    name: 'Tooltip',
    section: 'tooltip',
    importPath: 'Components/DesignSystem/Tooltip/Tooltip',
    typeFile: 'Tooltip.ts',
    summary:
      'The hover and focus bubble. It explains a control that is already labelled — it is never where the label itself lives, because a tooltip is not reachable by touch.',
    useWhen: [
      'A control needs a sentence of explanation that would not fit beside it.',
      'A disabled control should say why it is disabled.',
    ],
    insteadUse: [
      { component: 'FieldTitle', when: 'the explanation belongs to a form field — it has an info slot' },
      { component: 'Alert', when: 'everyone needs to read it, not just whoever hovers' },
    ],
    props: [
      { name: 'shortcut', type: 'string', description: 'A keyboard shortcut in the ARIA spelling (`"Meta+S"`), drawn as keycaps after the content and set as `aria-keyshortcuts` on the trigger. Display only — the caller binds the key.' },
      { name: 'content', type: 'React.ReactNode', required: true, description: 'What the bubble says.' },
      {
        name: 'children',
        type: 'React.ReactNode',
        required: true,
        description: 'The trigger. It must be a SINGLE element — it is cloned to receive the description link, and a fragment or bare string renders but silently loses it.',
      },
      { name: 'placement', type: "'top' | 'bottom' | 'left' | 'right'", default: "'top'", description: 'Preferred side. When that side has no room and the opposite has more, it flips there rather than covering its own trigger; it slides back inside the viewport on the other axis.' },
      { name: 'disabled', type: 'boolean', default: 'false', description: 'Renders the trigger and nothing else — no bubble, no listeners.' },
      { name: 'usePortal', type: 'boolean', default: 'true', description: 'The bubble renders into the body, so a table, a card or a drawer body cannot clip it — the same default as InfoTooltip and Dropdown. `false` renders it inline, inside its wrapper.' },
      { name: 'block', type: 'boolean', default: 'false', description: 'Lays the wrapper out as a full-width block, for a trigger that is itself a block element — an inline wrapper would shrink it.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'describeChild', type: 'boolean', default: 'true', description: 'Whether the bubble becomes the trigger\'s description while open. Pass false when it only repeats the trigger\'s name, or a screen reader hears it twice.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook. The bubble waits ~300ms on hover and appears instantly on keyboard focus, so a spec should await it rather than assert synchronously. It lives in the body, so find it at page level. Escape closes it from anywhere; inside a dialog the first Escape closes the tooltip and the next the dialog.' },
    ],
    snippet: `<Tooltip content="Recategorize is limited to 500 products at a time" usePortal>
  <span>
    <Button variant="secondary" disabled={selection.length > 500}>
      Recategorize
    </Button>
  </span>
</Tooltip>`,
  },

  {
    name: 'PageSkeleton',
    section: 'loading',
    importPath: 'Components/DesignSystem/PageSkeleton/PageSkeleton',
    typeFile: 'PageSkeleton.ts',
    summary:
      'A whole page in outline, for the first load — the breadcrumb, the title row, the toolbar and the table, in the shape the real page will take.',
    useWhen: [
      'A route is loading for the first time and nothing is on screen yet.',
      'The page shape is one of the four the presets cover.',
    ],
    insteadUse: [
      { component: 'Skeleton', when: 'only part of the page is waiting' },
      { component: 'Spinner', when: 'the wait is short and its shape is unknown' },
    ],
    props: [
      {
        name: 'preset',
        type: "'list' | 'detail' | 'summary-list' | 'explorer'",
        required: true,
        description: '`list` is breadcrumb, title, toolbar, table. `detail` is the add-edit form shape. `summary-list` puts a summary card above the child list and renders NO title row, because the card carries it. `explorer` is a two-column workspace — a bordered rail of item rows beside a detail card — that fills its container with no breadcrumb or title row, because it renders inside a shell whose header and tabs stay on screen.',
      },
      { name: 'label', type: 'string', default: "'Loading page'", description: 'Announced once. Name the thing — "Loading products", not a generic wait.' },
      { name: 'rows', type: 'number', default: '5', description: 'Table body rows, field rows for `detail`, or rail rows for `explorer`.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `if (isLoading) {
  return <PageSkeleton preset="list" label="Loading products" rows={8} />;
}`,
  },

  {
    name: 'Spinner',
    section: 'skeleton',
    importPath: 'Components/DesignSystem/Spinner/Spinner',
    typeFile: 'Spinner.ts',
    summary:
      'The indeterminate wait: something is happening and nobody knows for how long. It always carries a label, because a spinner nobody is told about is a silent wait — and the label reaches screen readers a moment after the spinner does, so it is heard as news rather than skipped.',
    useWhen: [
      'A short wait has no measurable progress and no predictable shape.',
      'A control is busy and the spinner belongs inside it.',
    ],
    insteadUse: [
      { component: 'Progress', when: 'you know what fraction is done' },
      { component: 'Skeleton', when: 'you know what shape is coming' },
    ],
    props: [
      { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'Mark size. `sm` is the one that fits inside a field or a button.' },
      { name: 'label', type: 'string', default: "'Loading'", description: 'Announced, and used as the hidden label. Narrow it whenever the page runs more than one wait.' },
      { name: 'showLabel', type: 'boolean', default: 'false', description: 'Renders the label as visible text beside the mark.' },
      { name: 'tone', type: "'accent' | 'inverse'", default: "'accent'", description: '`inverse` is the spinner on a FILL — ink-inverse arc over a 30% ring — which is what a loading primary Button renders. Everywhere else the token ring stays.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Spinner size="sm" label="Loading categories" showLabel data-testid="categories-spinner" />`,
  },

  {
    name: 'Skeleton',
    section: 'skeleton',
    importPath: 'Components/DesignSystem/Skeleton/Skeleton',
    typeFile: 'Skeleton.ts',
    summary:
      'A placeholder in the shape of the thing that is coming. Siblings share one shimmer, so a loading list reads as a single band of light sweeping the surface rather than N strips flashing together.',
    useWhen: [
      'You know the shape of the content before it arrives.',
      'Part of a page is waiting while the rest is usable.',
    ],
    insteadUse: [
      { component: 'PageSkeleton', when: 'the whole route is loading — it composes these for you' },
      { component: 'Spinner', when: 'the shape is unknown' },
    ],
    props: [
      { name: 'variant', type: "'text' | 'rect' | 'circle'", default: "'text'", description: 'What it is standing in for.' },
      { name: 'width', type: 'string', default: "'100%'", description: 'Any CSS length. Size from the layout.' },
      { name: 'height', type: 'string', description: 'Any CSS length. `text` derives its own from the type scale.' },
      { name: 'lines', type: 'number', default: '1', description: '`text` only. The last line is short, as real text is.' },
      {
        name: 'wave',
        type: 'number',
        default: '0',
        description: 'Phase into the shared shimmer. Pass each row its index and the sweep becomes ONE band crossing the list; leave it at 0 for a lone skeleton.',
      },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the placeholder.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `{rows.map((row, index) => (
  <Skeleton key={row.id} variant="text" wave={index} />
))}`,
  },

  {
    name: 'Progress',
    section: 'progress',
    importPath: 'Components/DesignSystem/Progress/Progress',
    typeFile: 'Progress.ts',
    summary:
      'The determinate bar. Spinner owns "something is happening"; this owns "62% of it is done".',
    useWhen: [
      'An import, upload or job reports a real percentage.',
      'The result of the wait has a state worth colouring — complete, degraded, failed.',
    ],
    insteadUse: [
      { component: 'Spinner', when: 'there is no measurable fraction' },
      { component: 'Status', when: 'the job state matters more than its progress' },
    ],
    props: [
      { name: 'value', type: 'number', required: true, description: '0 to 100, clamped — a fetch reporting 104% renders a full bar rather than an overflow.' },
      { name: 'label', type: 'string', required: true, description: 'Announced name: "Importing products", not just a number. Required, for the same reason Spinner has one.' },
      { name: 'showValue', type: 'boolean', default: 'false', description: 'Renders the percentage beside the bar, in tabular figures so it does not judder as it counts.' },
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: '4px inline or in a table cell, 8px page-level.' },
      { name: 'tone', type: "'accent' | 'success' | 'warning' | 'danger'", default: "'accent'", description: 'Accent while running; success complete, warning degraded, danger failed.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Templates `-bar` and `-value`.' },
    ],
    snippet: `<Progress
  value={importedPercent}
  label="Importing products"
  tone={failed ? 'danger' : 'accent'}
  showValue
  data-testid="import-progress"
/>`,
  },

  {
    name: 'Stepper',
    section: 'progress',
    importPath: 'Components/DesignSystem/Stepper/Stepper',
    typeFile: 'Stepper.ts',
    summary:
      'Where the user is in a fixed sequence of steps: an ordered list in a named nav, the current step marked, every other step\'s state in words, and going back as the only way to move.',
    useWhen: [
      'A task is split into a few ordered steps the user takes in turn — a wizard in a dialog.',
      'The user should see how far they are and what comes next.',
    ],
    insteadUse: [
      { component: 'Progress', when: 'the progress is a quantity (percent uploaded), not named steps' },
      { component: 'Tab', when: 'the parts can be visited in any order' },
    ],
    props: [
      { name: 'steps', type: 'StepperStep[]', required: true, description: '`{ id, label, description? }`, in order. The description is a second line: what the step asks for, or what was chosen in it.' },
      { name: 'current', type: 'string', required: true, description: 'The id of the step the user is on. The steps before it are complete, the ones after it upcoming. The flow owns it.' },
      { name: 'onStepChange', type: '(id: string) => void', description: 'Makes each completed step a button that reports its id, to go back and change an earlier answer. Upcoming steps never navigate: the flow\'s Next button decides when one is reachable.' },
      { name: 'orientation', type: "'horizontal' | 'vertical'", default: "'horizontal'", description: 'A row for a dialog header; a column for a side rail, where the descriptions have room.' },
      { name: 'aria-label', type: 'string', default: "'Progress'", native: true, description: 'The landmark\'s name. `aria-labelledby` pointing at the dialog title replaces it.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the nav.' },
      { name: 'data-testid', type: 'string', description: 'Lands on the nav; steps template `-step-<id>` (each with `data-status`: complete, current or upcoming), their buttons `-step-<id>-button`.' },
    ],
    snippet: `<Stepper
  steps={[
    { id: 'target', label: 'Choose target' },
    { id: 'attributes', label: 'Map attributes' },
    { id: 'review', label: 'Review' },
  ]}
  current={step}
  onStepChange={setStep}
  aria-labelledby="recategorize-title"
  data-testid="recategorize-steps"
/>`,
  },

  {
    name: 'EmptyState',
    section: 'empty-states',
    importPath: 'Components/DesignSystem/EmptyState/EmptyState',
    typeFile: 'EmptyState.ts',
    summary:
      'Nothing is here, and here is why — plus the one thing that would change that. It is the success path for an empty list, not an error.',
    useWhen: [
      'A list, table or panel has no rows and the reason is ordinary.',
      'There is an action that would fill it.',
    ],
    insteadUse: [
      { component: 'ErrorState', when: 'it is empty because something FAILED' },
      { component: 'Skeleton', when: 'it is empty because the data has not arrived yet' },
    ],
    props: [
      { name: 'title', type: 'string', required: true, description: 'One line, sentence case. What is not here.' },
      { name: 'description', type: 'React.ReactNode', description: 'Why it is not here, or what to do about it.' },
      { name: 'illustration', type: 'React.ReactNode', description: 'Page-level artwork above the text — pass `<NoDataIllustration />` for the house drawing. Prefer it over `icon` when the empty state IS the page.' },
      { name: 'icon', type: 'React.ReactNode', description: 'A smaller mark, for an empty panel or cell.' },
      { name: 'action', type: 'React.ReactNode', description: 'A Button, usually — the one thing that would fix the emptiness.' },
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: '`sm` inside a table cell or a panel, `md` for a page.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<EmptyState
  illustration={<NoDataIllustration />}
  title="No rules yet"
  description="Rules decide which products can be sold together."
  action={<Button variant="primary" onClick={createRule}>Create a rule</Button>}
  data-testid="rules-empty"
/>`,
  },

  {
    name: 'ImagePlaceholder',
    section: 'empty-states',
    importPath: 'Components/DesignSystem/ImagePlaceholder/ImagePlaceholder',
    typeFile: 'ImagePlaceholder.ts',
    summary:
      'The media slot with nothing in it — a product with no photo. One string is both the announced name and the printed caption, so the two cannot drift apart.',
    useWhen: [
      'A record has an image slot and no image.',
      'A grid must keep its rhythm whether or not every tile has artwork.',
    ],
    insteadUse: [
      { component: 'Skeleton', when: 'the image is still loading rather than missing' },
      { component: 'Avatar', when: 'the missing picture is a person' },
    ],
    props: [
      { name: 'label', type: 'string', default: "'No image available'", description: 'The accessible name, and the visible caption while `showLabel` is on. One string for both.' },
      { name: 'showLabel', type: 'boolean', default: 'true', description: 'Off for thumbnails too small to carry text — the name still announces either way.' },
      { name: 'width', type: 'string', default: "'100%'", description: 'Any CSS length. Size from the layout, like Skeleton.' },
      { name: 'height', type: 'string', description: 'Any CSS length.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<ImagePlaceholder
  label={\`No photo for \${product.name}\`}
  showLabel={false}
  height="96px"
  data-testid={\`product-\${product.id}-no-photo\`}
/>`,
  },

  {
    name: 'ErrorState',
    section: 'error-state',
    importPath: 'Components/DesignSystem/ErrorState/ErrorState',
    typeFile: 'ErrorState.ts',
    summary:
      'Something failed, said in the user words, with the technical half folded away one keystroke from a bug report.',
    useWhen: [
      'A fetch or a route failed and there is nothing to show.',
      'The user needs a retry, or a way back.',
    ],
    insteadUse: [
      { component: 'EmptyState', when: 'nothing failed — there is simply nothing there' },
      { component: 'Alert', when: 'the rest of the page still works' },
    ],
    props: [
      {
        name: 'title',
        type: 'string',
        default: "'Something went wrong'",
        description: 'One line, in the user words — what failed, not which layer threw. "We could not load this catalogue" beats "Unexpected Application Error".',
      },
      { name: 'description', type: 'React.ReactNode', description: 'What to do about it. A next step, not an apology.' },
      {
        name: 'detail',
        type: 'React.ReactNode',
        description: 'Status code, message, stack — rendered inside a CLOSED details element, so it is one keystroke from a bug report and invisible otherwise. A closed details is not announced either.',
      },
      { name: 'action', type: 'React.ReactNode', description: 'A Button — Retry or Go back.' },
      { name: 'icon', type: 'React.ReactNode', description: 'Overrides the default mark.' },
      { name: 'size', type: "'sm' | 'md'", default: "'md'", description: '`sm` inside a panel or table, `md` for a whole route.' },
      {
        name: 'role',
        type: 'React.AriaRole',
        default: "'alert'",
        description: 'Alert by default — an error surface that mounts and announces nothing is the failure this component exists to avoid. Note this is the opposite default from Status, because fifty status chips can render at once and fifty route errors cannot.',
      },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the wrapper.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<ErrorState
  title="We couldn't load this catalogue"
  description="The import service didn't respond. Your changes are safe."
  detail={\`\${error.status} · \${error.message}\`}
  action={<Button variant="primary" onClick={refetch}>Try again</Button>}
  data-testid="catalogue-error"
/>`,
  },

  /* ── Overlays ─────────────────────────────────────────────────────────────── */
  {
    name: 'Dialog',
    section: 'overlays',
    importPath: 'Components/DesignSystem/Dialog/Dialog',
    typeFile: 'Dialog.ts',
    summary:
      'The modal. It takes focus, traps it, makes the page behind it inert, closes on Escape, and hands focus back to whatever opened it — which is the whole reason to use this rather than a div with a scrim. Below 480px it gives its margins to the content: an 8px gutter, the full height, a 16px inset.',
    useWhen: [
      'The user must deal with something before carrying on.',
      'A destructive action needs confirming.',
    ],
    insteadUse: [
      { component: 'SidePanel', when: 'the user should still see the record behind it' },
      { component: 'Popover', when: 'it hangs off a trigger and need not be modal' },
      { component: 'Toast', when: 'nothing needs answering' },
    ],
    props: [
      { name: 'open', type: 'boolean', default: 'false', description: 'Controlled visibility. The one name every overlay takes.' },
      {
        name: 'isOpen',
        type: 'boolean',
        deprecated: 'Pass `open` — the name every overlay in the library takes. `open` wins when both are passed.',
        description: 'Still honoured, so existing call sites need no change.',
      },
      { name: 'onClose', type: '() => void', required: true, description: 'Called by Escape, the close button, and the scrim when that is enabled.' },
      { name: 'title', type: 'string', required: true, description: 'Header title — an `<h2>`, the level under the page title — and the accessible name.' },
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The body.' },
      { name: 'footer', type: 'React.ReactNode', description: 'The action band. Compose the buttons yourself — a destructive confirm wants `danger-primary`.' },
      { name: 'tone', type: "'danger' | 'warning'", description: 'Marks a destructive or risky confirm: the tone glyph appears beside the title. The footer buttons stay yours.' },
      {
        name: 'size',
        type: "'sm' | 'md' | 'lg' | 'xl' | 'half' | number",
        default: "'md'",
        description: '384 / 490 / 1000 / 1280px, a ramp that only grows. `half` is half the viewport with a 480px floor (the full width below a 512px viewport, so it never overflows one) — it exists so forms whose width was already viewport-relative could migrate without resizing, and it was called `xl` until the ramp was straightened. A number is an exact max-width in px. Reach for a fixed size first.',
      },
      { name: 'role', type: "'dialog' | 'alertdialog'", default: "'dialog'", description: '`alertdialog` for a question that interrupts — a confirm, usually about something that cannot be undone. Screen readers announce it as an alert and read its description. ConfirmDialog sets it for you.' },
      { name: 'aria-describedby', type: 'string', description: 'The id(s) of what describes the dialog — usually its message. An alert dialog should always have one.' },
      { name: 'initialFocusRef', type: 'React.RefObject<HTMLElement | null>', description: 'Where focus lands on open. By default the first control in the body, else the panel. A destructive confirm points it at its least destructive action.' },
      {
        name: 'closeOnOutsideClick',
        type: 'boolean',
        default: 'false',
        description:
          'Off by default on purpose: a dialog holding unsaved edits should not discard them to a stray click beside it. Opt in when the dialog is a PICKER rather than an editor. A portalled Dropdown inside counts as inside.',
      },
      { name: 'headerActions', type: 'React.ReactNode', description: 'Controls between the title and the close button.' },
      {
        name: 'headerLeading',
        type: 'React.ReactNode',
        description: 'Content BEFORE the title — a back arrow, for a dialog opened from another. It is not `headerActions`, because back is a leading affordance everywhere, and two dismissals side by side are indistinguishable at a glance.',
      },
      { name: 'showCloseButton', type: 'boolean', default: 'true', description: 'Set false when dismissal is expressed another way. Escape and the scrim are unaffected — this hides a button, it does not trap anyone.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the panel.' },
      { name: 'aria-labelledby', type: 'string', native: true, description: 'Native — everything not listed above, and the ref, reach the panel (the role="dialog" element). A caller\'s aria-labelledby, or an aria-label given without one, replaces the title as the name.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook.' },
    ],
    snippet: `<Dialog
  open={confirmOpen}
  onClose={() => setConfirmOpen(false)}
  title="Delete this rule?"
  tone="danger"
  size="sm"
  footer={(
    <>
      <Button variant="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
      <Button variant="danger-primary" onClick={remove}>Delete rule</Button>
    </>
  )}
  data-testid="rule-delete-dialog"
>
  Products already using it keep their associations.
</Dialog>`,
  },

  {
    name: 'SidePanel',
    section: 'overlays',
    importPath: 'Components/DesignSystem/SidePanel/SidePanel',
    typeFile: 'SidePanel.ts',
    summary:
      'The drawer: a modal surface that slides in from the edge and keeps the record behind it in view, to read but not to use: the page behind is inert until it closes. Optionally tabbed, with the tab row pinned under the header. Never narrower than the viewport or than `md` — below 480px every width is the whole screen, with a 16px inset.',
    useWhen: [
      'The user edits or inspects something without losing their place in the list.',
      'The content is long, or splits into tabs.',
    ],
    insteadUse: [
      { component: 'Dialog', when: 'it is a short confirm and the background is a distraction' },
      { component: 'Accordion', when: 'the detail can live inline' },
    ],
    props: [
      { name: 'open', type: 'boolean', default: 'false', description: 'Controlled visibility. The one name every overlay takes.' },
      {
        name: 'isOpen',
        type: 'boolean',
        deprecated: 'Pass `open` — the name every overlay in the library takes. `open` wins when both are passed.',
        description: 'Still honoured, so existing call sites need no change.',
      },
      { name: 'onClose', type: '() => void', required: true, description: 'Called by Escape and the close control.' },
      { name: 'title', type: 'React.ReactNode', required: true, description: 'Panel title. A string names the dialog itself; a composed node names it by its rendered text — pass `aria-label` when that text is not the right name.' },
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The body.' },
      {
        name: 'tabs',
        type: 'SidePanelTabConfig<TabId>[]',
        description: 'The tab row. Omitted or `[]`, there is no tab row. (Omitting it used to render the Environment editor\'s three tabs as a back-compat default; that editor passes them itself now.)',
      },
      { name: 'activeTab', type: 'TabId', description: 'Selected tab id. Controlled.' },
      { name: 'onTabChange', type: '(tab: TabId) => void', description: 'Fires with the new tab id. Generic over your own id union, so your setter still type-checks.' },
      { name: 'headerActions', type: 'React.ReactNode', description: 'Replaces the legacy Cancel/Save pair. The drawer stays a bare surface — what the actions do is yours.' },
      {
        name: 'width',
        type: "'md' | 'lg' | 'xl' | '2xl'",
        default: "'md'",
        description: '416px, 600px for a dense edit panel, `xl` at 66% of the viewport for a panel holding a TABLE, and `2xl` at 70% of the viewport held between 600px and 920px, for a panel denser than a form (fields plus a picker and a matrix). Do not reach for `xl` for a form — a 66%-wide column of fields is far worse than a 416px one. The fixed widths never exceed the viewport: in a narrow window, or at 400% zoom, the drawer is full width.',
      },
      { name: 'loading', type: 'boolean', default: 'false', description: 'Marks the panel aria-busy; the default Save/Cancel pair disables itself but stays focusable, so the Save you just pressed keeps focus. Before this open has shown its content it is the FIRST load, and the body shows the loading treatment. Once the content has shown, a later `loading` (a save, a refetch) keeps it on screen, inert and busy, and Save reads "Saving..." — a save never swaps the form out.' },
      {
        name: 'isLoading',
        type: 'boolean',
        deprecated: 'Pass `loading` — the busy flag every component in the library takes.',
        description: 'Still honoured, so existing call sites need no change.',
      },
      {
        name: 'closeOnOutsideClick',
        type: 'boolean',
        default: 'true',
        description: 'Whether a click on the scrim closes the drawer. Pass `false` for a drawer holding edits a stray click must not discard. Escape and your own close controls still close it. (Dialog\'s equivalent defaults to `false`.)',
      },
      {
        name: 'aria-label',
        type: 'string',
        description: 'The accessible name, when the title is not it. A string title names the dialog by itself; a node title names it by its rendered text (it used to leave the dialog unnamed).',
      },
      {
        name: 'ariaLabel',
        type: 'string',
        deprecated: 'Pass `aria-label` — the native spelling every component takes. It wins when both are passed.',
        description: 'Still honoured, so existing call sites need no change.',
      },
      { name: 'onSave', type: '(data: unknown) => Promise<void> | void', deprecated: 'Compose your own buttons through `headerActions`.', description: 'Only consumed by the legacy default header.' },
      { name: 'onSaveError', type: '(error: unknown) => void', description: 'Receives a rejection from `onSave`. Omitted, the legacy Save logs it to the console, as before.' },
      { name: 'subheader', type: 'React.ReactNode', description: 'A row under the title inside the header band, so it never scrolls away: the identity of what the drawer shows. Not for controls — those are headerActions.' },
      { name: 'footer', type: 'React.ReactNode', description: 'A row pinned to the bottom of the drawer, below the scroll container and behind a top rule, so it stays put however short or long the content is: the bottom-edge twin of subheader. Omitted, nothing renders. Tagged `${data-testid}-footer`.' },
      { name: 'className', type: 'string', default: "''", description: 'Merged onto the panel: a conflicting class replaces the panel\'s own.' },
      { name: 'id', type: 'string', native: true, description: 'Native — everything not listed above, and the ref, reach the panel (the role="dialog" element).' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook; tabs derive `-tab-${id}`.' },
    ],
    snippet: `<SidePanel
  open={!!editing}
  onClose={() => setEditing(null)}
  title={editing?.name ?? ''}
  width="lg"
  tabs={[
    { id: 'details', label: 'Details' },
    { id: 'availability', label: 'Availability', required: true },
  ]}
  activeTab={tab}
  onTabChange={setTab}
  headerActions={<Button variant="primary" size="sm" onClick={save}>Save</Button>}
  data-testid="product-edit-panel"
>
  <ProductForm product={editing} />
</SidePanel>`,
  },

  {
    name: 'Popover',
    section: 'popover',
    importPath: 'Components/DesignSystem/Popover/Popover',
    typeFile: 'Popover.ts',
    summary:
      'The anchored surface — non-modal, hung off a trigger, for the things a Menu cannot hold and a Dialog is too heavy for: a filter form, a colour picker, a confirm with a field in it.',
    useWhen: [
      'A trigger opens something that needs inputs rather than a list of commands.',
      'The user should still be able to see and use the page behind it.',
    ],
    insteadUse: [
      { component: 'Menu', when: 'the content is a list of actions' },
      { component: 'Dialog', when: 'the page behind must be blocked until it is answered' },
      { component: 'Tooltip', when: 'it only explains something and takes no input' },
    ],
    props: [
      { name: 'open', type: 'boolean', required: true, description: 'Controlled only — the caller owns the state, because the content usually does too (a draft filter that applies on Done).' },
      { name: 'onOpenChange', type: '(open: boolean) => void', required: true, description: 'Fires on trigger, Escape and outside dismiss.' },
      {
        name: 'trigger',
        type: 'React.ReactElement',
        required: true,
        description: 'The toggling element. It is cloned with the popup wiring and an onClick composed AFTER its own — a handler calling preventDefault cancels the toggle.',
      },
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The surface content. Focus moves in on open and back to the trigger on close.' },
      { name: 'aria-label', type: 'string', native: true, required: true, description: 'The surface accessible name. A dialog must have one, so the type asks.' },
      {
        name: 'ariaLabel',
        type: 'string',
        deprecated: 'Pass `aria-label` — the native spelling every component takes. It wins when both are passed. The type accepts either until its call sites move.',
        description: 'Still honoured, so existing call sites need no change.',
      },
      { name: 'placement', type: "'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'", default: "'bottom-start'", description: 'The vertical half is a PREFERENCE — the surface flips when the viewport runs out and reports what it did through `data-placement`.' },
      { name: 'closeOnOutsideClick', type: 'boolean', default: 'true', description: 'Turn it off for a surface holding a draft a stray click must not discard.' },
      { name: 'usePortal', type: 'boolean', default: 'true', description: 'The surface renders into the body at the corner `placement` names, on the `popover` rung, so nothing clips it, and slides back inside the viewport rather than crossing its edge. It stays on screen: it flips to the side with room, caps itself at the room that side has (and scrolls), and hides when its trigger is scrolled out of view. Tab past its last control closes it and returns focus to the trigger. `false` is the inline surface.' },
      { name: 'className', type: 'string', default: "''", description: 'Lands on the surface, composed after the chrome.' },
      { name: 'data-testid', type: 'string', description: 'Playwright hook on the surface.' },
    ],
    snippet: `<Popover
  open={filtersOpen}
  onOpenChange={setFiltersOpen}
  aria-label="Filter products"
  closeOnOutsideClick={false}
  trigger={<Button variant="subtle" startIcon={<Filter />}>Filters</Button>}
  data-testid="products-filter-popover"
>
  <FilterForm draft={draft} onApply={apply} />
</Popover>`,
  },

  /* ── Foundations ──────────────────────────────────────────────────────────── */
  {
    name: 'Typography',
    section: 'typography',
    importPath: 'Components/DesignSystem/Typography/Typography',
    typeFile: 'Typography.ts',
    summary:
      'Every piece of text in the app. The variant names the JOB — page title, section heading, body, caption — and the element follows from it, so type size and document structure stay two separate decisions. Sizes are rem: exactly the px shown at the browser\'s default, and larger for a reader who set a larger default font size.',
    useWhen: [
      'You are about to write text. All of it goes through here.',
      'A heading needs a level that differs from its size.',
    ],
    insteadUse: [
      { component: 'FieldTitle', when: 'the text is naming a form control' },
      { component: 'Kbd', when: 'it is a key name' },
    ],
    props: [
      {
        name: 'variant',
        type: "'display-2' | 'display' | 'heading-1' | 'heading-2' | 'heading-3' | 'title-header' | 'title' | 'body-large' | 'body-primary' | 'sub-title-header' | 'sub-title-label' | 'sub-body-supporting' | 'caption-metadata' | 'code-formula'",
        required: true,
        description: 'The job the text is doing. A page title is `display` at 24px — that pairing is enforced app-wide; `heading-2` is the SECTION heading inside a page, not the title of it.',
      },
      {
        name: 'as',
        type: "'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div' | 'code' | 'label'",
        description:
          'The element, when it must differ from the one the variant implies. Pass it whenever the visual rung and the outline disagree — that separation is the point of the prop. `label` is here so a field label can BE the token rather than wrap one.',
      },
      { name: 'children', type: 'React.ReactNode', required: true, description: 'The text.' },
      { name: 'className', type: 'string', default: "''", description: 'Colour and spacing go here — always a role token, never a palette step.' },
    ],
    snippet: `<Typography variant="display" as="h1" className="tw-text-ink">
  Product catalogue
</Typography>

<Typography variant="body-primary" className="tw-text-ink-secondary">
  {product.description}
</Typography>`,
  },

  {
    name: 'Iconography',
    section: 'iconography',
    importPath: 'Components/DesignSystem/Iconography',
    typeFile: null,
    usage: 'module',
    summary:
      'Every icon, from one import: the CORE set (drawn to the 16 grid at a 1.5 stroke — reach for these first) and the APP set (the product’s own glyphs, grouped by job). Every glyph draws in currentColor and takes a `size`, so an icon inside a button or a tone wears that ink for free — never give one a colour of its own.',
    useWhen: [
      'A control or a row needs a mark.',
      'A tone needs its glyph, so that colour is not the only thing carrying the meaning.',
    ],
    insteadUse: [
      { component: 'Avatar', when: 'the mark stands for a person' },
      { component: 'ImagePlaceholder', when: 'it stands for missing artwork' },
    ],
    props: [
      { name: 'size', type: 'number | string', description: 'Pixel box, per glyph default. The core set sits on 16/20/24 grids; do not scale one to an off-grid size. A non-square app glyph keeps its proportions.' },
      { name: 'aria-hidden', type: 'boolean', default: 'true (unless named)', description: 'Every glyph is decorative by default — hidden from assistive tech. Name one with `aria-label` and it is exposed as an image with that name instead; pass `aria-hidden` yourself to decide either way.' },
      { name: 'className', type: 'string', description: 'Placement only. Ink comes from the parent through currentColor.' },
    ],
    snippet: `import { Edit, Delete, Refresh, BooleanIcon } from 'Components/DesignSystem/Iconography';

<IconButton aria-label="Edit rule" variant="subtle">
  <Edit />
</IconButton>`,
  },
];

/** Every documented component, by the section that demos it. */
export const docsForSection = (sectionId: string): ComponentDoc[] =>
  COMPONENT_DOCS.filter((doc) => doc.section === sectionId);

/**
 * The ratchet, now empty — every component directory under `DesignSystem/` has a reference
 * entry, and `componentDocs.test.tsx` asserts exactly that.
 *
 * It is kept rather than deleted, because the fault it guards against is not "the docs were
 * never written", it is "a component was added and nobody wrote one". An empty set makes
 * that failure loud: a new directory belongs to neither the registry nor this list, and the
 * coverage test names it. Adding a component back here is a deliberate, reviewable act, not
 * something that happens by omission.
 */
export const UNDOCUMENTED = new Set<string>([]);
